/**
 * Game state → UI view models.
 *
 * The UI deliberately imports nothing from `combat/`, `sim/`, `entity/` or
 * `meta/` — it defines its own plain interfaces and the integrator maps into
 * them. That decoupling is what let the UI be built and verified against mock
 * data in parallel with everything else, and this file is the seam that makes
 * it real.
 *
 * The adapter mutates one persistent object graph rather than rebuilding it.
 * `UIRoot.update()` dirty-checks every value, so allocating fresh view models
 * at 60 Hz would defeat its own optimisation and add GC pressure besides.
 *
 * The *reference tables* — every weapon, module, material, reaction and enemy —
 * never change after boot, so their view models are built once in the
 * constructor and only their volatile fields (equipped, installed, discovered,
 * favourite, track values) are refreshed, and then only when a monotonic
 * signature says something actually moved. The per-frame path reads a handful
 * of numbers and returns.
 *
 * A reference table is not an inventory, though, and conflating the two is how
 * the loadout panel came to list all 40 weapons in the game with MAIN/OFF
 * buttons on gear the player could never hold. `UIFrameState.owned` is the
 * run's actual holdings; the tables behind it are indexed by id and the owned
 * set selects from them. The full catalogue survives only as an explicit debug
 * mode (`UIAdapterSources.devCatalogue`), labelled as such in the panel.
 */

import {
  MELEE_CLASSES,
  PROJECTILE_CLASSES,
  StatusKind,
  STATUS_NAMES,
  WeaponTag,
  type Hand,
  type ModuleDef,
  type ModuleEffect,
  type PrimerKindId,
  type Rarity as CombatRarity,
  type ResolvedWeapon,
  type WeaponClass,
  type WeaponDef,
  type WeaponInstance,
} from '../combat/types';
import { hasStatus } from '../combat/status';
import { DEFAULT_BINDINGS } from './input';
import { bindingRows } from '../ui/settingsModel';
import { GATHER_TIERS } from '../combat/gather';
// The same lookup `combat/gather.ts` names its log line with, so the material
// the HUD warns about and the material the run log warns about are spelled once.
import { materialName } from '../combat/harvest';
import { hitRateHz } from '../combat/weaponData';
import { CORE_SHARE, resolveWeapon } from '../combat/resolve';
import {
  newHandResources,
  readHandResources,
  rhythmDecaying,
  type HandResources,
  type HandState,
  type WielderState,
} from '../combat/wielder';
import { Chosen } from '../entity/abilities';
import { Ability, flightCapacity, type MoverState } from '../entity/movement';
import { Comp, DAMAGE_TYPE_NAMES, World } from '../entity/ecs';
import {
  EnemyRegistry,
  FAMILY_NAMES,
  RANK_NAMES,
  bodyCue,
  resistancesOf,
  weaknessesOf,
  type CompEntry,
  type EnemyDef,
} from '../entity/enemyData';
import { MAT, registerMaterials, type MaterialName } from '../sim/materialData';
import {
  CATEGORY_NAMES,
  Flag,
  allMaterials,
  materialDef,
  tagNamesOf,
  type MaterialDef,
} from '../sim/materials';
import { buildReactionRules } from '../sim/reactionData';
import type { ReactionRule } from '../sim/reactions';
import {
  Progression,
  TRACKS,
  TRACK_IDS,
  unlocksForTrack,
  type TrackId,
} from '../meta/progression';
import {
  AUTOPSY_LOCK_HINT,
  reactionHint,
  redactEnemyEntry,
  resolvePassives,
  type RunPassives,
} from '../meta/passives';
import {
  abilities as runAbilities,
  causeHeadline,
  causeSentence,
  formatRunTime,
  type BankReport,
  type RunState,
} from '../meta/runState';
import {
  createFactionState,
  DEEDS,
  FACTION_BY_ID,
  FACTION_IDS,
  Factions,
  OPPOSED_PAIRS,
  REP_MAX,
  type AxisId,
  type DeedId,
  type FactionId,
  type FactionState,
} from '../meta/factions';
import {
  asMapRoomKind,
  computeSynergy,
  repMarker01,
  targetSlotFor,
  type AbilityVM,
  type FactionAxisVM,
  type FactionsVM,
  type FactionStanding,
  type FactionVM,
  gameOverActions,
  type GameOverRowVM,
  type GameOverSectionVM,
  type GameOverVM,
  type VendorOfferVM,
  type CodexRefVM,
  type EnemyEntryVM,
  type GameVM,
  type HandVM,
  type IndexedThumbVM,
  type InventoryVM,
  rhythmBonusText,
  type MapRoomVM,
  type MapVM,
  type MaterialVM,
  type ModuleVM,
  type PlayerVM,
  type Rarity as UIRarity,
  type ReactionEntryVM,
  type RhythmVM,
  type SlotVM,
  type StatModVM,
  type StatVM,
  type StatusVM,
  type SynergyHintVM,
  type TrackVM,
  type WeaponVM,
} from '../ui/viewModels';

/**
 * One glyph per weapon class, so the HUD never has to guess (HandVM.glyph).
 * Keyed by the literal `WeaponClass` ids — the three compound classes are
 * spelled out because a near-miss key silently degrades to the fallback glyph
 * and nothing complains.
 */
const CLASS_GLYPH: Record<WeaponClass, string> = {
  sword: '†', dagger: '⸸', claw: 'ϟ', gauntlet: '✊', spear: '↑', lance: '⇑',
  greatblade: '⊤', hammer: '⊓', axe: '⌐', scythe: '⌒', whip: '∿', chain: '⛓',
  shield: '⌂', staff: 'ǁ', relicBlade: '❖', drill: '⌁', living: '❦',
  anchor: '⚓',
  pistol: '•', rifle: '—', scattergun: '≡', launcher: '◎', bow: '⌉',
  rail: '═', shardCaster: '✦', beam: '⌇', drone: '◇', sprayer: '≈', bio: '❀',
  thrownRig: '◈',
};

/**
 * Display names. `ModuleVM.compatibleClasses` is matched against these strings
 * by `moduleFitsWeapon`, so the two must come from one table or the
 * compatibility filter quietly hides everything.
 */
const CLASS_NAME: Record<WeaponClass, string> = {
  sword: 'Sword', dagger: 'Dagger', claw: 'Claw', gauntlet: 'Gauntlet',
  spear: 'Spear', lance: 'Lance', greatblade: 'Greatblade', hammer: 'Hammer',
  axe: 'Axe', scythe: 'Scythe', whip: 'Whip', chain: 'Chain', shield: 'Shield',
  staff: 'Staff', relicBlade: 'Relic Blade', drill: 'Drill', living: 'Living',
  anchor: 'Anchor',
  pistol: 'Pistol', rifle: 'Rifle', scattergun: 'Scattergun',
  launcher: 'Launcher', bow: 'Bow', rail: 'Rail', shardCaster: 'Shard Caster',
  beam: 'Beam', drone: 'Drone', sprayer: 'Sprayer', bio: 'Bio',
  thrownRig: 'Thrown Rig',
};

/** The palette has one colour per UI rarity; `exotic` is the name that differs. */
const RARITY: Record<CombatRarity, UIRarity> = {
  common: 'common', uncommon: 'uncommon', rare: 'rare', exotic: 'epic', relic: 'relic',
};

/**
 * Which module family a weapon leans into. `WeaponDef` has no family field —
 * damage type *is* the theme, which is why the whole module table is organised
 * the same way (docs/04 §5.2).
 */
const FAMILY_BY_DAMAGE: readonly string[] = [
  'kinetic', 'pyre', 'frost', 'galvanic', 'corrosive', 'fungal',
  'void', 'radiant', 'blood', 'temporal', 'fungal', 'nanite',
];

const MODULE_FAMILY_GLYPH: Record<string, string> = {
  frost: '❄', pyre: '🜂', void: '◌', blood: '🜄', fungal: '✿', galvanic: 'ϟ',
  corrosive: '⌾', nanite: '⚙', radiant: '☀', temporal: '⧗', kinetic: '⊙',
  resonant: '♪',
};

const TRACK_GLYPH: Record<TrackId, string> = {
  ash: '🜃', codex: '❖', lineage: '☠', salvage: '⚙', attunement: '◎',
};

/**
 * The verb the synergy prose uses for each primer a weapon consumes. Bare
 * "detonates" reads as a number; "shatters" reads as a plan (docs/04 §7.1).
 */
const DETONATE_VERB: Record<number, string> = {
  [StatusKind.BURNING]: 'ignites', [StatusKind.FROZEN]: 'shatters',
  [StatusKind.SHOCKED]: 'discharges', [StatusKind.CORRODED]: 'dissolves',
  [StatusKind.BLEEDING]: 'harvests', [StatusKind.INFECTED]: 'blooms',
  [StatusKind.MARKED]: 'executes', [StatusKind.PHASED]: 'collapses',
  [StatusKind.STAGGERED]: 'punishes', [StatusKind.OILED]: 'ignites',
  [StatusKind.CURSED]: 'consumes', [StatusKind.IRRADIATED]: 'detonates',
};

const WEAPON_TAG_NAMES: ReadonlyArray<readonly [number, string]> = Object.entries(WeaponTag).map(
  ([k, v]) => [v as number, k.toLowerCase().replace(/_/g, '-')] as const,
);

/** VARIANT_* are a packed field rather than single bits, so they are excluded. */
const CELL_FLAG_NAMES: ReadonlyArray<readonly [number, string]> = [
  [Flag.WET, 'wet'], [Flag.BURNING, 'burning'], [Flag.CHARGED, 'charged'],
  [Flag.FROZEN, 'frozen'], [Flag.CORRODING, 'corroding'], [Flag.INFECTED, 'infected'],
  [Flag.LIT, 'lit'], [Flag.PRESSURIZED, 'pressurised'], [Flag.PHASED, 'phased'],
  [Flag.SANCTIFIED, 'sanctified'], [Flag.CURSED, 'cursed'], [Flag.STATIC, 'static'],
];

/**
 * Which cooldown timer on `MoverState` each ability spends, if any.
 *
 * Named rather than indexed because the four are read back through a measured
 * length (see `trackCooldowns`) rather than through a copy of the constant.
 */
type CooldownKey = 'breach' | 'blink' | 'jet' | 'phase';

/** No key at all is a different statement from a key we could not resolve. */
const NO_KEY = '—';

/**
 * Which key produces each action, before anyone has said otherwise.
 *
 * #129 removed the literals `'L'` and `'⇧'` from the ability strip in favour of
 * this, and #134 made it the *fallback* rather than the answer: it is what the
 * HUD prints until `setBindings` hands over the table the game is actually
 * being played on. An adapter constructed by a test, or by a `Game` before its
 * first frame, still prints something true of a fresh profile.
 */
const DEFAULT_KEY_LABELS: ReadonlyMap<string, string> = new Map(
  bindingRows(DEFAULT_BINDINGS, {}).map((r) => [r.action, r.key]),
);

/**
 * Every key cap the HUD draws, and the action each one resolves through.
 *
 * The list is here rather than spread across the fill methods because #134's
 * audit is the point: *these* are the labels, and every one of them goes
 * through `keyLabelFor`. Adding a surface that prints a key without appearing
 * here is the defect reappearing, and the audit test walks this table.
 *
 *   - the two hand cards, which said the literals `LMB` / `RMB` — set once at
 *     construction, never updated, and not even the keyboard default;
 *   - the ability strip's single cap (`ability`);
 *   - the nine ability tooltips, seven of which are that same `ability`, one
 *     the dash, and Cling none at all.
 *
 * The dash pips draw no key: `Hud.updateDash` writes `data-full` and a fill
 * fraction and nothing else. That is recorded here so the next audit does not
 * have to rediscover it.
 */
export const HUD_KEY_ACTIONS: readonly string[] = ['mainAttack', 'offAttack', 'ability', 'dash'];

/**
 * The nine traversal abilities, as the HUD lists them.
 *
 * `keyAction` used to be a hard-coded key, and before that it claimed W, S, ↑
 * and Shift. Seven of the nine are *the same action* — `ability` — and the
 * context selector picks between them (docs/02 §6.5); a hint that names a
 * different key per ability describes a control scheme this game does not have.
 * Airstep is the one that really is on another key: it grants an air dash, so
 * its key is the dash key. Cling has none at all — it happens on wall contact,
 * and `null` says that rather than naming a button nobody presses.
 *
 * `chosen` is the `Chosen` id the selector resolves to for this row, and is what
 * makes the armed preview possible. Airstep and Cling are not selectable, so
 * they map to `Chosen.NONE` and can never light up.
 */
const ABILITY_LIST: Array<{
  bit: number; id: string; name: string; glyph: string; keyAction: string | null;
  chosen: number; cooldown: CooldownKey | null; charges: boolean;
}> = [
  { bit: Ability.AIRSTEP, id: 'airstep', name: 'Airstep', glyph: '⇢', keyAction: 'dash', chosen: Chosen.NONE, cooldown: null, charges: false },
  { bit: Ability.CLING, id: 'cling', name: 'Cling', glyph: '⊏', keyAction: null, chosen: Chosen.NONE, cooldown: null, charges: false },
  { bit: Ability.TETHER, id: 'tether', name: 'Tether', glyph: '⟜', keyAction: 'ability', chosen: Chosen.TETHER, cooldown: null, charges: false },
  { bit: Ability.BREACH, id: 'breach', name: 'Breach', glyph: '⊘', keyAction: 'ability', chosen: Chosen.BREACH, cooldown: 'breach', charges: false },
  { bit: Ability.BLINK, id: 'blink', name: 'Blink', glyph: '⇋', keyAction: 'ability', chosen: Chosen.BLINK, cooldown: 'blink', charges: true },
  { bit: Ability.BURROW, id: 'burrow', name: 'Burrow', glyph: '⊻', keyAction: 'ability', chosen: Chosen.BURROW, cooldown: null, charges: false },
  { bit: Ability.FLUID_ADAPT, id: 'fluid', name: 'Fluid Adapt', glyph: '≋', keyAction: 'ability', chosen: Chosen.FLUID_JET, cooldown: 'jet', charges: false },
  { bit: Ability.PHASE_DRIFT, id: 'phase', name: 'Phase Drift', glyph: '⌁', keyAction: 'ability', chosen: Chosen.PHASE, cooldown: 'phase', charges: false },
  { bit: Ability.UPDRAFT, id: 'updraft', name: 'Updraft', glyph: '⇡', keyAction: 'ability', chosen: Chosen.UPDRAFT, cooldown: null, charges: false },
];

/**
 * One glyph per status. Shape carries the meaning, colour only reinforces it
 * (docs/01 §6.4.1) — three simultaneous statuses have to stay countable, and
 * they can't be if they're all coloured squares.
 */
const STATUS_GLYPH: Record<number, string> = {
  [StatusKind.BURNING]: '🜂', [StatusKind.FROZEN]: '❄', [StatusKind.SHOCKED]: 'ϟ',
  [StatusKind.CORRODED]: '⌾', [StatusKind.BLEEDING]: '🜄', [StatusKind.INFECTED]: '❦',
  [StatusKind.MARKED]: '◎', [StatusKind.PHASED]: '◌', [StatusKind.STAGGERED]: '✖',
  [StatusKind.OILED]: '◍', [StatusKind.CURSED]: '☠', [StatusKind.IRRADIATED]: '☢',
  [StatusKind.SLOWED]: '⊤', [StatusKind.HASTED]: '⊥', [StatusKind.WEAKENED]: '↓',
  [StatusKind.ARMOR_SHRED]: '⊗', [StatusKind.LIFELINK]: '♡', [StatusKind.RESONATING]: '♪',
  [StatusKind.ECHOED]: '⧉', [StatusKind.NANITE_BOUND]: '⚙', [StatusKind.OVERHEATED]: '🜃',
  [StatusKind.SPORED]: '✿',
  [StatusKind.DROWNING]: '🫧',
};

/** The few statuses that help you. Everything else is a debuff. */
const BENEFICIAL = new Set<number>([
  StatusKind.HASTED, StatusKind.LIFELINK, StatusKind.RESONATING,
]);

/** Loadout slots and quick pairs are fixed-size by docs/04 §7. */
const WEAPON_SLOTS = 8;
const LOADOUT_SLOTS = 6;
const QUICK_PAIRS = 2;

/**
 * What the panel says when no stash view was supplied at all (#5).
 *
 * The only sentence about the stash this file owns. Both real reasons — no
 * slots bought, not standing at a rest node — come from `STASH_LOCK_HINTS` in
 * `meta/stash.ts` and ride through on `UIStashView.hint`, because meta owns the
 * gate and a second copy of the wording here is a second thing to keep in step.
 * This is the fallback for an integrator that has not wired the stash yet, and
 * it is deliberately the *shut* answer rather than the open one.
 */
const STASH_NO_SLOTS = 'No stash yet — Ash buys the slots on the run-setup screen.';

/** The same fallback, for the workbench. `meta/crafting.ts` owns the real ones. */
const CRAFT_NO_BENCH = 'Crafting needs a rest node — find a workbench.';

/**
 * What the rhythm meter is actually worth, read off the two places that apply
 * it: `resolve.ts` multiplies damage by `1 + rhythm * 0.35`, and `wielder.ts`
 * multiplies recovery by `1 - rhythm * 0.3`. The HUD used to print 25% and 18%,
 * which were nobody's numbers — a meter that misstates its own payoff is worse
 * than one that says nothing, because the player prices their play off it.
 *
 * These are duplicated constants and the duplication is the risk: `tests/
 * ui.test.ts` pins the rendered string, and if combat retunes the meter this
 * pair is the one thing that has to move with it.
 */
const RHYTHM_DAMAGE_FRAC = 0.35;
const RHYTHM_RECOVERY_FRAC = 0.3;

/**
 * The captured-thumbnail budget, stated rather than assumed.
 *
 * docs/01 argues the save format is a seed plus a diff log *precisely* to stay
 * small, so a feature that adds images to it has to name its ceiling. One byte
 * per cell, 16×16 cells, 96 entries: 24 KiB of pixel data, ~33 KB once base64
 * and the per-row keys are counted. 16 cells across is also about the largest
 * region that still reads as a *moment* at 64px on screen rather than as a map.
 *
 * The cap is set above the reaction table's current 68 rows with an `event` on
 * purpose. A cap *below* the table size does not bound anything the player can
 * perceive — it just means the last few reactions they discover are silently
 * the ones with no photograph, which reads as the feature being broken. It
 * still bounds the save against a table that keeps growing, which is the thing
 * the constraint is actually for.
 */
export const THUMB_DIM = 16;
export const THUMB_CAP = 96;
export const THUMB_BUDGET_BYTES = THUMB_DIM * THUMB_DIM * THUMB_CAP;
/** Bumped when the serialised layout changes; old blobs are then ignored. */
const THUMB_FORMAT = 't1';

/**
 * All the capture path needs from the cell world.
 *
 * Structural on purpose: the adapter is the seam and has no business holding
 * `CellWorld`'s whole surface just to read 256 cells once per discovery.
 */
export interface CellPeek {
  peek(x: number, y: number): number;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_INV = (() => {
  const t = new Int16Array(128).fill(-1);
  for (let i = 0; i < B64.length; i++) t[B64.charCodeAt(i)] = i;
  return t;
})();

/**
 * Local base64 rather than `btoa`. The adapter runs under the game, under
 * vitest and (via the save file) under whatever reads it back; `btoa` is a
 * browser global that is only *usually* present elsewhere, and a save path that
 * throws on one runtime is a save path that silently loses the store.
 */
function b64encode(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + B64[n & 63];
  }
  const rest = bytes.length - i;
  if (rest === 1) {
    const n = bytes[i] << 16;
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + '==';
  } else if (rest === 2) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8);
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + '=';
  }
  return out;
}

function b64decode(s: string): Uint8Array {
  const clean = s.replace(/=+$/, '');
  const out = new Uint8Array((clean.length * 3) >> 2);
  let acc = 0;
  let bits = 0;
  let w = 0;
  for (let i = 0; i < clean.length; i++) {
    const code = clean.charCodeAt(i);
    const v = code < 128 ? B64_INV[code] : -1;
    // A corrupted blob must decode to nothing rather than to a plausible
    // wrong picture — the caller checks the length and drops the row.
    if (v < 0) return new Uint8Array(0);
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[w++] = (acc >> bits) & 0xff;
    }
  }
  return out.subarray(0, w);
}

/** Room ids are chunk coordinates, `"cx,cy"` — see Game.trackLocation. */
const ROOM_NEIGHBOURS: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
];

// ------------------------------------------------------------------ factions

/** Stands in for `run.factions` before a run exists, so the panel has shape. */
const EMPTY_FACTION_STATE: FactionState = createFactionState();
const ZERO_AXES: Readonly<Record<AxisId, number>> = { veil: 0, substrate: 0, continuance: 0 };

/**
 * One `FactionVM` per faction, allocated once and refilled in place.
 *
 * `wants` and `gives` are static table text and are written here rather than in
 * the per-frame refill: they never change, and re-reading them 60 times a second
 * to write the same strings back is exactly the churn the budget forbids.
 */
function newFactionVM(id: FactionId): FactionVM {
  const def = FACTION_BY_ID[id];
  return {
    id,
    name: def.name,
    reputation: 0,
    standing: 'neutral',
    shopOpen: true,
    offerTier: 1,
    hostile: false,
    hostileRate: 0,
    wants: def.wants,
    gives: def.gives,
  };
}

/**
 * Three bars, not six, and the pairing comes from `OPPOSED_PAIRS` rather than
 * being restated — the pair *is* the axis (docs/05 §5), and a second listing of
 * who opposes whom is a second thing that can drift out of step.
 */
function buildFactionsVM(): FactionsVM {
  const axes: FactionAxisVM[] = OPPOSED_PAIRS.map(([a, b]) => ({
    id: FACTION_BY_ID[a].axis,
    pos: newFactionVM(FACTION_BY_ID[a].pole === 1 ? a : b),
    neg: newFactionVM(FACTION_BY_ID[a].pole === 1 ? b : a),
    marker01: 0.5,
    untouched: true,
  }));
  return { revision: 0, axes, deeds: [], untouched: true };
}

/** Every field here is derived by `Factions` — never stored, never recomputed. */
function fillFaction(vm: FactionVM, f: Factions): void {
  const id = vm.id as FactionId;
  vm.reputation = f.reputation(id);
  vm.standing = f.standing(id);
  vm.shopOpen = f.shopOpen(id);
  vm.offerTier = f.offerTier(id);
  vm.hostile = f.hostile(id);
  vm.hostileRate = f.hostileEncounterRate(id);
}

// ------------------------------------------------------------------ helpers

function primerNames(kinds: readonly number[]): string[] {
  return kinds.map((k) => (STATUS_NAMES[k] ?? 'primer').toUpperCase());
}

function tagNames(mask: number): string[] {
  const out: string[] = [];
  for (const [bit, name] of WEAPON_TAG_NAMES) if ((mask & bit) !== 0) out.push(name);
  return out;
}

function flagNames(mask: number): string {
  const out: string[] = [];
  for (const [bit, name] of CELL_FLAG_NAMES) if ((mask & bit) !== 0) out.push(name);
  return out.join('+');
}

function cssColor(m: MaterialDef): string {
  return `rgb(${m.color.r},${m.color.g},${m.color.b})`;
}

function pct(v: number): string {
  return Math.round(v * 100) + '%';
}

function strHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h | 0;
}

/**
 * Change detector for the run's holdings.
 *
 * `update()` runs every frame inside a 0.5 ms budget, so the owned set is
 * hashed rather than diffed: a few dozen character codes, against a rebuild
 * that walks every owned instance and every module slot. Ids are short and the
 * set is small (8 slots plus a stash), so this stays in the noise.
 */
function ownedSignature(owned: UIOwned | null | undefined): number {
  if (!owned) return 0;
  let h = Math.imul(owned.weapons.length + 1, 2654435761) | 0;
  for (const inst of owned.weapons) {
    // The *instance* id, not the definition: swapping one roll of a weapon for
    // another roll of the same weapon is a real change to the inventory, and a
    // signature over definitions cannot see it at all.
    h = (Math.imul(h, 31) + strHash(inst.id)) | 0;
    for (const m of inst.modules) h = (Math.imul(h, 31) + strHash(m)) | 0;
  }
  h = (Math.imul(h, 31) + owned.modules.length) | 0;
  for (const m of owned.modules) h = (Math.imul(h, 31) + strHash(m)) | 0;
  return h;
}

/**
 * Change detector for the stash, on the same argument as `ownedSignature`.
 *
 * `capacity` and `open` are in it as well as the contents: buying a slot
 * changes the store's shape without changing what is in it, and walking into a
 * rest node changes nothing at all except whether the buttons work. Both have
 * to redraw the column, and neither moves the item list.
 */
/**
 * Change detector for the workbench.
 *
 * The pouch is the volatile part — every harvest moves a number — so the units
 * are hashed rather than the recipe list, which only moves on an unlock. The
 * craftable flags ride along because they are a function of both.
 */
function craftingSignature(view: UICraftingView | null | undefined): number {
  if (!view) return 0;
  let h = Math.imul(view.pouchUsed + 1, 2654435761) | 0;
  h = (Math.imul(h, 31) + view.recipes.length * 2 + (view.open ? 1 : 0)) | 0;
  for (const p of view.pouch) {
    h = (Math.imul(h, 31) + strHash(p.material) + p.units) | 0;
  }
  for (const r of view.recipes) {
    h = (Math.imul(h, 31) + strHash(r.id) + (r.craftable ? 1 : 0)) | 0;
  }
  return h;
}

function stashSignature(view: UIStashView | null | undefined): number {
  if (!view) return 0;
  let h = Math.imul(view.capacity + 1, 2654435761) | 0;
  h = (Math.imul(h, 31) + view.items.length * 2 + (view.open ? 1 : 0)) | 0;
  for (const it of view.items) {
    h = (Math.imul(h, 31) + strHash(it.id)) | 0;
    for (const m of it.modules) h = (Math.imul(h, 31) + strHash(m)) | 0;
  }
  return h;
}

/**
 * Deliberately NOT cast with `as HandVM`. The cast is what let `ammoMax` go
 * unset and render as the literal string "null/undefined" in the HUD — an
 * omission the compiler would otherwise have caught for free.
 *
 * The compiler can only force the field to *exist*, though, and for a while
 * that was mistaken for having fixed it: `ammo`/`ammoMax` stayed null because
 * nothing in combat/ has a magazine to report. `fillHand` says so explicitly
 * now rather than leaving the silence to be re-read as an oversight.
 */
function emptyHand(hand: Hand, keyHint: string): HandVM {
  const vm: HandVM = {
    hand,
    keyHint,
    weaponId: null,
    instanceId: null,
    name: '—',
    glyph: '·',
    rarity: 'common',
    comboStep: -1,
    comboLength: 0,
    comboNodeName: '',
    ready01: 1,
    ready: true,
    charge01: null,
    heat01: null,
    overheated: false,
    ammo: null,
    ammoMax: null,
    reloading: false,
    reload01: 1,
    reserve01: null,
    reserveKind: '',
    detonationArmed: false,
  };
  return vm;
}

// -------------------------------------------------------------- weapon → VM

const NO_MODULES: ReadonlyMap<string, ModuleDef> = new Map();

/**
 * A weapon with every roll at the neutral value.
 *
 * The panel is definition-level — `instanceVM` clones the catalogue row — so
 * the timings it prints are the *authored* weapon's, and pinning the rolls to
 * identity is what makes `resolveWeapon` answer that question instead of the
 * question "what did this particular drop happen to roll".
 *
 * Built per call and thrown away: `weaponStats` runs once per definition at
 * boot, not per frame.
 */
function neutralResolve(def: WeaponDef): ResolvedWeapon {
  return resolveWeapon(
    def,
    {
      id: `${def.id}#catalogue`,
      defId: def.id,
      modules: [],
      rolled: {
        seed: 0, damageMul: 1, cadenceMul: 1, reachMul: 1,
        weightAdd: 0, critChanceAdd: 0, critMulAdd: 0,
      },
      level: 1,
      favorite: false,
      charge: -1,
      heat: 0,
      ammo: -1,
    },
    NO_MODULES,
  );
}

/**
 * Swings per second, as the player will actually experience them: one light
 * node run windup → active → recovery, then the hand back to idle.
 *
 * This is the **opener** rate, which #39 ruled canonical out of the three that
 * existed (authored / opener / chained). Every band in docs/15 is quoted
 * against it, so the panel quotes it too — a UI that used a different one of
 * the three would put the player and the design doc in different games.
 *
 * The number comes out of `hitRateHz` in the combat layer, never out of a local
 * copy of the formula. `combat.test.ts:1189` looked green for months against a
 * transcription of exactly this arithmetic, and three more copies were found in
 * Phases 1–2.
 *
 * `hitRateHz` also handles the case this adapter briefly got wrong: a held kiln
 * beam re-contacts every `CONTINUOUS_HIT_MS`, so quoting its node rate would
 * have printed 3.29/s for a weapon that ticks at 10/s — swapping the old wrong
 * number for a new one. One meaning, two correct derivations, one definition.
 */
function weaponStats(def: WeaponDef): StatVM[] {
  // Was `def.cadenceMulBase` on both rows, which is not a rate at all — it is
  // the reference point of the `cadenceMul` chain and it cancels out of its own
  // formula (see `WeaponDef.cadenceMulBase`). The panel printed a unitless
  // number with a `/s` suffix and multiplied it by damage to get a "nominal
  // dps" no weapon had ever dealt: `ashen_sword` read 2.1/s and opens at
  // 7.81/s. FIXME(#39) resolved — the ruling named the opener.
  const rate = hitRateHz(neutralResolve(def));
  return [
    { key: 'damage', label: 'damage', value: def.damage, format: 'int' },
    { key: 'dps', label: 'dps (nominal)', value: def.damage * rate, format: 'int' },
    { key: 'cadence', label: 'cadence', value: rate, format: 'dec1', unit: '/s' },
    { key: 'reach', label: 'reach', value: def.reach, format: 'int', unit: 'u' },
    { key: 'windup', label: 'windup', value: def.windup, format: 'ms', higherIsBetter: false },
    { key: 'active', label: 'active', value: def.active, format: 'ms' },
    { key: 'recovery', label: 'recovery', value: def.recovery, format: 'ms', higherIsBetter: false },
    { key: 'poise', label: 'poise break', value: def.poise, format: 'dec1' },
    { key: 'weight', label: 'weight', value: def.weight, format: 'dec2', higherIsBetter: false },
    { key: 'dig', label: 'digs hardness ≤', value: def.terrain.destroy.maxHardness, format: 'int' },
  ];
}

/** docs/04 §6 in plain language — a number nobody can picture teaches nothing. */
function terrainText(def: WeaponDef): string {
  const t = def.terrain;
  const parts: string[] = [];
  if (t.destroy.maxHardness <= 0 || t.destroy.radius <= 0) {
    parts.push('leaves terrain alone');
  } else {
    parts.push(
      `destroys hardness ≤ ${t.destroy.maxHardness} within ${t.destroy.radius} units` +
        (t.destroy.chance < 1 ? ` (${pct(t.destroy.chance)} of contacts)` : ''),
    );
  }
  if (t.emit) parts.push(`sprays ${materialDef(t.emit.mat).label}`);
  if (t.heat) parts.push(t.heat > 0 ? `heats cells +${t.heat}°` : `chills cells ${t.heat}°`);
  if (t.charge) parts.push('injects charge into conductive matter');
  if (t.corrode) parts.push('corrodes what it touches');
  if (t.displaces) parts.push('pushes loose matter aside instead of deleting it');
  return parts.join(' · ');
}

function weaponCodexRefs(def: WeaponDef): CodexRefVM[] | undefined {
  const out: CodexRefVM[] = [];
  const seen = new Set<string>();
  const add = (mat: number): void => {
    const m = materialDef(mat);
    if (!m || seen.has(m.name)) return;
    seen.add(m.name);
    out.push({ kind: 'material', id: m.name, label: m.label });
  };
  if (def.emits) add(def.emits.mat);
  if (def.terrain.emit) add(def.terrain.emit.mat);
  return out.length > 0 ? out : undefined;
}

function buildWeaponVM(def: WeaponDef): WeaponVM {
  const primes = primerNames(def.primes);
  const detonates = primerNames(def.detonates);
  const slots: SlotVM[] = [];
  for (const spec of def.slots) {
    for (let i = 0; i < spec.count; i++) slots.push({ kind: spec.kind, index: i, moduleId: null });
  }

  const emitted = def.emits ?? def.terrain.emit;
  const vm: WeaponVM = {
    id: def.id,
    // The catalogue row is one-per-definition and is not a holding; instance
    // rows are cloned off this by `instanceVM`.
    instanceId: null,
    name: def.name,
    className: CLASS_NAME[def.class],
    archetype: def.archetype,
    rarity: RARITY[def.rarity],
    glyph: CLASS_GLYPH[def.class],
    damageType: DAMAGE_TYPE_NAMES[def.damageType],
    family:
      (def.tags & WeaponTag.RESONANT) !== 0
        ? 'resonant'
        : (FAMILY_BY_DAMAGE[def.damageType] ?? null),
    tags: tagNames(def.tags),
    stats: weaponStats(def),
    primes,
    detonates,
    terrainText: terrainText(def),
    effectText: def.desc,
    slots,
    favourite: false,
  };

  if (detonates.length > 0) vm.detonateVerb = DETONATE_VERB[def.detonates[0]] ?? 'detonates';
  if (primes.length > 0) {
    vm.primeVerb = emitted
      ? `sprays ${materialDef(emitted.mat).label} and applies ${primes.join('/')}`
      : `applies ${primes.join('/')}`;
  }
  // The tag mask is the only self-risk signal a WeaponDef carries; modules
  // carry a written warning, weapons do not, so this states the class of risk
  // rather than inventing a magnitude.
  if ((def.tags & WeaponTag.SELF_RISK) !== 0) {
    vm.selfRiskText = 'self-risk class: its payload can land on you as readily as on the target';
  }
  const refs = weaponCodexRefs(def);
  if (refs) vm.codexRefs = refs;
  return vm;
}

// -------------------------------------------------------------- module → VM

function statMods(e: ModuleEffect): StatModVM[] {
  const out: StatModVM[] = [];
  const mul = (key: string, label: string, v: number | undefined, better = true): void => {
    if (v === undefined || v === 1) return;
    out.push({ key, label, delta: v, mode: 'mul', format: 'x', higherIsBetter: better });
  };
  const add = (
    key: string, label: string, v: number | undefined,
    format: StatModVM['format'], better = true,
  ): void => {
    if (!v) return;
    out.push({ key, label, delta: v, mode: 'add', format, higherIsBetter: better });
  };

  mul('damage', 'damage', e.damageMul);
  mul('cadence', 'cadence', e.cadenceMul);
  mul('windup', 'windup', e.windupMul, false);
  mul('recovery', 'recovery', e.recoveryMul, false);
  mul('knockback', 'knockback', e.knockbackMul);
  mul('poise', 'poise break', e.poiseMul);
  mul('reach', 'reach', e.reachMul);
  mul('primed', 'damage vs primed', e.primedDamageMul);
  mul('detonation', 'detonation payload', e.detonationMul);
  add('weight', 'weight', e.weightAdd, 'dec2', false);
  add('crit', 'crit chance', e.critChanceAdd, 'pct');
  add('critMul', 'crit multiplier', e.critMulAdd, 'dec2');
  add('lifesteal', 'lifesteal', e.lifestealFrac, 'pct');
  add('count', 'projectiles', e.projectile?.countAdd, 'int');
  add('pierce', 'pierce', e.projectile?.pierceAdd, 'int');
  add('ricochet', 'ricochets', e.projectile?.ricochet, 'int');
  mul('speed', 'projectile speed', e.projectile?.speedMul);
  mul('spread', 'spread', e.projectile?.spreadMul, false);
  mul('arc', 'swing arc', e.melee?.arcMul);
  return out;
}

function buildModuleVM(def: ModuleDef): ModuleVM {
  const fitsMelee = def.fits !== 'projectile';
  const fitsProj = def.fits !== 'melee';
  const effects: ModuleEffect[] = [];
  if (fitsMelee) effects.push(def.onMelee);
  if (fitsProj) effects.push(def.onProjectile);

  const union = (pick: (e: ModuleEffect) => readonly number[] | undefined): string[] => {
    const seen = new Set<number>();
    for (const e of effects) for (const k of pick(e) ?? []) seen.add(k);
    return primerNames([...seen]);
  };

  const detonates = union((e) => e.detonates);
  const damageType = effects.find((e) => e.damageTypeOverride !== undefined)?.damageTypeOverride;
  const lines = [def.desc];
  if (fitsMelee) lines.push('melee: ' + def.onMelee.summary);
  if (fitsProj) lines.push('projectile: ' + def.onProjectile.summary);

  const vm: ModuleVM = {
    id: def.id,
    name: def.name,
    family: def.family,
    slot: def.slot,
    rarity: RARITY[def.rarity],
    glyph: MODULE_FAMILY_GLYPH[def.family] ?? '◆',
    tags: tagNames(def.tags),
    effectText: lines.join(' — '),
    primes: union((e) => e.primes),
    detonates,
    statMods: statMods(effects[0] ?? def.onMelee),
    // `fits` is archetype-wide, so compatibility is every class of that
    // archetype. `moduleFitsWeapon` matches these against `WeaponVM.className`.
    compatibleClasses:
      def.fits === 'both'
        ? null
        : def.fits === 'melee'
          ? MELEE_CLASSES.map((c) => CLASS_NAME[c])
          : PROJECTILE_CLASSES.map((c) => CLASS_NAME[c]),
    installedOn: null,
    favourite: false,
  };

  if (damageType !== undefined) vm.damageType = DAMAGE_TYPE_NAMES[damageType];
  if (detonates.length > 0) {
    // The synergy line quotes this verbatim, so it has to be the payoff
    // sentence rather than the module's flavour text.
    vm.detonationPayoff = (effects.find((e) => (e.detonates ?? []).length > 0) ?? effects[0])
      .summary;
  }
  if (def.selfRisk) vm.selfRiskText = def.selfRisk.warning;
  return vm;
}

// ------------------------------------------------------------ material → VM

function materialProps(d: MaterialDef): StatVM[] {
  const out: StatVM[] = [];
  const push = (
    key: string, label: string, value: number,
    format: StatVM['format'], unit?: string, higherIsBetter?: boolean,
  ): void => {
    const s: StatVM = { key, label, value, format };
    if (unit !== undefined) s.unit = unit;
    if (higherIsBetter !== undefined) s.higherIsBetter = higherIsBetter;
    out.push(s);
  };

  push('density', 'density', d.density, 'int', ' kg/m³');
  push('hardness', 'hardness', d.hardness, 'dec1');
  push('brittleness', 'brittleness', d.brittleness, 'pct', undefined, false);
  push('friction', 'friction', d.friction, 'dec2');
  push('temp', 'resting temp', d.temp, 'int', '°');
  push('conductivity', 'heat conductivity', d.conductivity, 'dec2');
  // Everything below is only worth a row when the material actually has it —
  // a codex full of zeroes is a codex nobody reads.
  if (d.ignitionTemp !== null) push('ignition', 'ignites at', d.ignitionTemp, 'int', '°');
  if (d.meltsAt !== null) push('melts', 'melts at', d.meltsAt, 'int', '°');
  if (d.freezesAt !== null) push('freezes', 'freezes at', d.freezesAt, 'int', '°');
  if (d.boilsAt !== null) push('boils', 'boils at', d.boilsAt, 'int', '°');
  if (d.electricConductivity > 0) push('electric', 'conducts charge', d.electricConductivity, 'pct');
  if (d.corrosiveness > 0) push('corrosive', 'corrosiveness', d.corrosiveness, 'pct');
  if (d.corrosionResist > 0) push('resist', 'corrosion resist', d.corrosionResist, 'pct');
  if (d.toxicity > 0) push('toxic', 'toxicity', d.toxicity, 'pct');
  if (d.bioActivity > 0) push('bio', 'bio-activity', d.bioActivity, 'pct');
  if (d.resonance > 0) push('resonance', 'resonance', d.resonance, 'pct');
  if (d.sanctity > 0) push('sanctity', 'sanctity', d.sanctity, 'pct');
  if (d.temporalStability !== 1) push('temporal', 'temporal stability', d.temporalStability, 'dec2');
  if (d.emissive > 0) push('emissive', 'emits light', d.emissive, 'pct');
  if (d.lifetime > 0) push('lifetime', 'dissipates after', d.lifetime, 'int', ' ticks');
  return out;
}

// ------------------------------------------------------------ reaction → VM

function sideLabel(side: number, isTag: boolean): string {
  if (!isTag) return materialDef(side)?.label ?? '?';
  const names = tagNamesOf(side);
  // A broad mask names its whole family; past three the row stops being a
  // sentence, so it becomes "anything X, Y or Z-like".
  return names.length <= 3
    ? names.map((n) => n.toLowerCase()).join('/')
    : names.slice(0, 3).map((n) => n.toLowerCase()).join('/') + '/…';
}

function reactionCondition(r: ReactionRule): string {
  const parts: string[] = [];
  if (r.minTemp !== undefined) parts.push(`at ${r.minTemp}° or hotter`);
  if (r.maxTemp !== undefined) parts.push(`at ${r.maxTemp}° or colder`);
  if (r.requiresFlagA) parts.push(`the first is ${flagNames(r.requiresFlagA)}`);
  if (r.requiresFlagB) parts.push(`the second is ${flagNames(r.requiresFlagB)}`);
  if (r.forbidsFlagA) parts.push(`the first is not ${flagNames(r.forbidsFlagA)}`);
  if (r.forbidsFlagB) parts.push(`the second is not ${flagNames(r.forbidsFlagB)}`);
  parts.push(`${pct(r.chance)} of contacts`);
  return parts.join(', ');
}

function reactionOutcome(r: ReactionRule, a: string, b: string): string {
  const parts: string[] = [];
  const side = (label: string, o: ReactionRule['outA']): void => {
    if (o === undefined || o === 'keep') return;
    parts.push(o === 'consume' ? `${label} is consumed` : `${label} becomes ${materialDef(o).label}`);
  };
  side(a, r.outA);
  side(b, r.outB);
  if (r.flagA) parts.push(`${a} turns ${flagNames(r.flagA)}`);
  if (r.flagB) parts.push(`${b} turns ${flagNames(r.flagB)}`);
  if (r.heat) parts.push(r.heat > 0 ? `releases ${r.heat}° of heat` : `absorbs ${-r.heat}° of heat`);
  if (r.emit) parts.push(`throws off ${r.emit.count}× ${materialDef(r.emit.mat).label}`);
  if (r.charge) parts.push('charges the contact');
  return parts.length > 0 ? parts.join(', ') : 'nothing you can see, but something changed';
}

/**
 * Colours for the codex row's swatch. `ReactionEntryVM.thumb` is a captured
 * frame and nothing captures frames yet, so every row rendered an empty 64px
 * box — which reads as a broken image, not as a pending feature. The materials
 * involved are known at build time, so the row can at least show the true
 * colour of what went in and what came out. A tag side has no single colour and
 * stays null, which the panel renders as hatching rather than as black.
 */
function reactionSwatch(r: ReactionRule): ReactionEntryVM['swatch'] {
  const side = (id: number, isTag: boolean): string | null =>
    isTag ? null : (materialDef(id) ? cssColor(materialDef(id)) : null);
  const product = r.outA !== undefined && r.outA !== 'keep' && r.outA !== 'consume'
    ? r.outA
    : r.outB !== undefined && r.outB !== 'keep' && r.outB !== 'consume'
      ? r.outB
      : r.emit?.mat;
  return {
    a: side(r.a, r.aIsTag),
    b: side(r.b, r.bIsTag),
    out: product !== undefined && materialDef(product) ? cssColor(materialDef(product)) : null,
  };
}

// ------------------------------------------------------------- enemy → VM

/**
 * Weakness rows must state *why* or the codex is a cheat sheet rather than a
 * teacher (docs/03 §6). The reason is the composition and the tags on it,
 * because that is literally what `susceptibility()` reads.
 */
function weaknessReason(def: EnemyDef): string {
  // The two named are the two the number most came from, which is not the same
  // as the two largest. The reading gives the core material an extra
  // `CORE_SHARE` on top of its fraction, so ranking by `fraction` alone can
  // leave the core out of its own explanation -- four bodies did exactly that,
  // and in all four the omitted material *is* the top row (#136).
  const share = (c: CompEntry): number => c.fraction + (c.mat === def.coreMat ? CORE_SHARE : 0);
  const top = [...def.composition].sort((x, y) => share(y) - share(x)).slice(0, 2);
  const body = top.map((c) => `${pct(c.fraction)} ${materialDef(MAT[c.mat]).label}`).join(' + ');
  const tags = new Set<string>();
  for (const c of top) for (const t of tagNamesOf(materialDef(MAT[c.mat]).tags)) tags.add(t.toLowerCase());
  const tagList = [...tags].slice(0, 4).join(', ');
  return tagList ? `${body} — ${tagList}` : body;
}

/**
 * Everything `redactEnemyEntry` empties, kept so it can be put back.
 *
 * The redaction mutates the view model in place — which is right, because the
 * codex rebuilds into the same pooled objects every revision — but that makes
 * it destructive, and the passive it gates on can be bought at any time. The
 * unredacted rows live here and are copied back the moment autopsy is held.
 */
interface EnemyDisclosure {
  composition: EnemyEntryVM['composition'];
  weaknesses: EnemyEntryVM['weaknesses'];
  resistances: EnemyEntryVM['resistances'];
}

function buildEnemyVM(def: EnemyDef): EnemyEntryVM {
  const reason = weaknessReason(def);
  return {
    id: def.id,
    name: def.name,
    discovered: false,
    killCount: 0,
    deathsTo: 0,
    composition: def.composition.map((c) => ({
      mat: materialDef(MAT[c.mat]).label,
      fraction: c.fraction,
      color: cssColor(materialDef(MAT[c.mat])),
    })),
    weaknesses: weaknessesOf(def).map((w) => ({
      label: `${DAMAGE_TYPE_NAMES[w.type].toUpperCase()} ×${w.multiplier.toFixed(2)}`,
      reason,
    })),
    resistances: resistancesOf(def).map((w) => ({
      label: `${DAMAGE_TYPE_NAMES[w.type].toUpperCase()} ×${w.multiplier.toFixed(2)}`,
      reason,
    })),
    behaviour: `${RANK_NAMES[def.rank]} ${FAMILY_NAMES[def.family]}`,
    // Outside the Composition Autopsy redaction on purpose: this is the
    // pre-fight tell, and hiding it behind the passive that hides the weakness
    // rows would make the cue and its confirmation the same unlock.
    cue: (() => {
      const f = bodyCue(def);
      return {
        name: f.name,
        tell: f.tell,
        keys: f.keys.map((t2) => DAMAGE_TYPE_NAMES[t2].toUpperCase()).join(' / '),
      };
    })(),
    // Set by `applyDisclosure` on the first refresh; the row is never published
    // in this default state.
    redacted: false,
    redactedHint: '',
  };
}

// ------------------------------------------------------------------ adapter

/**
 * Registries the adapter reads. All optional so the call site can adopt them
 * one at a time; anything missing is built from the same content tables the
 * game uses, which keeps the panels populated instead of blank.
 */
export interface UIAdapterSources {
  enemies?: EnemyRegistry;
  progression?: Progression;
  reactionRules?: readonly ReactionRule[];
  /**
   * Show every weapon and module in the game rather than the run's holdings.
   * A content-authoring affordance, off by default: the panel is an inventory,
   * and an inventory that lists gear you cannot obtain teaches the player that
   * its buttons do not work.
   */
  devCatalogue?: boolean;
}

/**
 * What the profile actually holds. Rows are keyed by `WeaponInstance.id` (#12),
 * so two rolls of one definition are two rows with their own modules.
 */
export interface UIOwned {
  /** Carried instances. The two equipped hands are added automatically. */
  weapons: readonly WeaponInstance[];
  /** Every module the run holds, installed or loose. */
  modules: readonly string[];
}

/**
 * One row of the profile stash, structurally `StashItem` from `meta/stash.ts`.
 *
 * Declared here rather than imported for the same reason `CellPeek` is: the
 * adapter is the seam and takes the narrowest shape that answers the question.
 * Structural means the integrator passes `Stash.view(atRestNode)` through
 * untouched, so there is no mapping layer to fall out of sync.
 */
export interface UIStashItem {
  id: string;
  defId: string;
  kind: 'weapon' | 'module';
  /** Display name captured at deposit, so a renamed definition still reads. */
  label: string;
  modules: readonly string[];
}

/** Structurally `StashVM` from `meta/stash.ts` — `Stash.view(atRestNode)`. */
export interface UIStashView {
  capacity: number;
  used: number;
  items: readonly UIStashItem[];
  open: boolean;
  /** Why it is shut, in meta's words. Empty when open. */
  hint: string;
}

/**
 * Structurally `CraftingVM` from `meta/crafting.ts` — `craftingVM(...)`.
 *
 * Same seam as the stash: meta owns the pouch, the recipe gate and every
 * refusal sentence, and this file only adds the display labels, because a row
 * reading "WATER 12/30" names a constant rather than a material.
 */
export interface UICraftingView {
  open: boolean;
  hint: string;
  recipes: readonly {
    id: string;
    name: string;
    desc: string;
    tier: string;
    verb: string;
    inputs: readonly { material: string; need: number; have: number }[];
    craftable: boolean;
    needs: readonly string[];
    hint: string;
  }[];
  pouch: readonly { material: string; units: number }[];
  pouchUsed: number;
  pouchCap: number;
}

/**
 * A loadout snapshot resolved against the current holdings (#17).
 *
 * `exact` is the field the integrator must not ignore: a restore is allowed to
 * be partial — that is better than refusing outright when one module went
 * missing — but the player has to be told, because the whole failure this
 * issue names is a build that came back different under the same name.
 */
export interface LoadoutRestore {
  mainId: string | null;
  offId: string | null;
  /** Module ids to install on the main-hand weapon, in the saved order. */
  mainModules: string[];
  offModules: string[];
  /** Display names of everything the snapshot named and the profile lost. */
  missing: string[];
  /** Nothing was dropped — this reproduces the saved build exactly. */
  exact: boolean;
}

/** Per-frame state that lives outside the ECS. */
export interface UIFrameState {
  run?: RunState | null;
  /** Stash access is rest-node gated; respec never is (docs/04 §7). */
  atRestNode?: boolean;
  /**
   * The profile stash, exactly `Stash.view(atRestNode)` from `meta/stash.ts`
   * (#5). Passed through rather than derived here for the same reason
   * `biomeHazard` is: meta owns the capacity rule, the access gate and the
   * sentence that explains a closed store, and a second copy of any of the
   * three in this file is a second thing to keep in step. Omitted, the panel
   * reads a store with no slots — which is the truth for a profile that has
   * bought none, and is never silently "open".
   */
  stash?: UIStashView | null;
  /**
   * The rest-node workbench, exactly `craftingVM(run, prog, seen, atRestNode)`
   * from `meta/crafting.ts` (#8). Same argument as `stash`: meta owns the
   * pouch, the codex gate and every refusal sentence. Omitted, the workbench
   * reads as shut with no recipes, which is the truth before it is wired.
   */
  crafting?: UICraftingView | null;
  /**
   * The run's holdings. Omitted, the panel shows exactly what is in the two
   * hands — which is the truth for a run that has no loot system yet, and is
   * the one thing the old "whole catalogue" default was not.
   */
  owned?: UIOwned | null;
  /**
   * The current biome's `BiomeHazard.desc`. The adapter deliberately does not
   * reach into `world/` for it: the integrator already resolves the biome once
   * per frame in `trackLocation`, so passing the sentence through costs nothing
   * and keeps the biome tables out of the seam.
   */
  biomeHazard?: string | null;
  /**
   * What kind of room a chunk is — `Game.roomKind(cx, cy)`, verbatim (#25).
   *
   * A function rather than a table because the room graph is chunk-keyed and
   * open-ended: the map emits unvisited neighbours too, and pre-tabulating a
   * kind for every coordinate the player might one day be adjacent to is work
   * for rooms nobody will look at. `refreshMap` is dirty-gated, so this is
   * called only on the frames the map actually rebuilds.
   *
   * Omitted, every room reads `normal`, which is the truth for an integrator
   * with no worldgen — never a guess.
   */
  roomKind?: (cx: number, cy: number) => string;
  /**
   * The vendor the player is standing at (#11), or null/omitted for the common
   * case of standing at none.
   *
   * Pass-through, for the same reason `stash` and `crafting` are: the vendor
   * owns its own stock, its standing-scaled prices and every sentence it refuses
   * with, and a second copy of any of the three here is a second thing to keep
   * in step. The one thing this file adds is affordability, because the purse is
   * on `RunState` and the vendor should not have to be handed it twice.
   */
  vendor?: UIVendorInput | null;
  /**
   * The gathering field, if a hold is open (#122 / #129).
   *
   * A field-for-field copy of what the integrator already holds, for the same
   * reason `vendor` and `stash` are pass-throughs: `combat/gather.ts` owns the
   * hold ladder and `sim/attract.ts` owns the hazard scan, and a second copy of
   * either rule here is a second thing to keep in step. Omitted, the field
   * reads as closed — which is the truth for an integrator that has none, and
   * is never a guess about a field that is open.
   */
  gather?: UIGatherInput | null;
}

/**
 * What the integrator hands over for the gathering field.
 *
 * Shaped so the glue in `Game.update` is a field-for-field copy off
 * `GatherState` and `AttractField` with nothing derived on the way. In
 * particular the distances stay in **cells**, the unit both of those work in;
 * turning one into a fraction of the radius is this file's job, because the
 * radius is also here and deriving it at the source would mean the same
 * arithmetic in two places.
 */
export interface UIGatherInput {
  /** `GatherState.active`. */
  active: boolean;
  /** `GatherState.tier` — index into `GATHER_TIERS`, -1 while closed. */
  tier: number;
  /** `AttractField.radius`, in cells. */
  radius: number;
  /** `AttractField.hazardDist`, in cells. `Infinity` when there is none. */
  hazardDist: number;
  /** `AttractField.hazardMat` — a `MatId`, `VOID` (0) for none. */
  hazardMat: number;
  /** `AttractField.hazardCount` — eligible hazardous cells inside the radius. */
  hazardCount: number;
}

/**
 * One line of a vendor's stock — `world/vendor.ts` `VendorShelfEntry`, narrowed
 * to what a panel can render. `label` is its `name`; `refusal` is its own.
 */
export interface UIVendorOfferInput {
  id: string;
  label: string;
  price: number;
  /** A refusal the vendor itself has — standing depth, sold out, hostility. */
  refusal?: string;
}

/**
 * What the world hands over for a vendor in range (#11).
 *
 * Shaped to `world/vendor.ts` `VendorStatus` plus `shelf()`, so the integrator's
 * glue is a field-for-field copy and nothing is derived twice. The one thing
 * this file adds is affordability, because the purse is on `RunState` and the
 * vendor should not have to be handed it a second time.
 */
export interface UIVendorInput {
  factionId: string;
  factionName: string;
  standing: FactionStanding;
  inRange: boolean;
  open: boolean;
  hostile?: boolean;
  refusal: string;
  offers: readonly UIVendorOfferInput[];
  /** Which line is on the counter. Omitted, the first takeable line is. */
  counterId?: string | null;
  /** Seconds until the counter turns over. */
  rotateIn?: number;
}

export class UIAdapter {
  readonly vm: GameVM;
  private statusPool: StatusVM[] = [];
  private abilityPool: AbilityVM[] = [];

  private readonly enemyReg: EnemyRegistry;
  private readonly prog: Progression;

  private readonly weaponVMs: WeaponVM[] = [];
  private readonly moduleVMs: ModuleVM[] = [];
  private readonly weaponById = new Map<string, WeaponVM>();
  private readonly moduleById = new Map<string, ModuleVM>();
  /** Reaction rule ids in table order, parallel to `vm.codex.reactions`. */
  private readonly reactionEvents: string[] = [];
  /** Unredacted enemy rows, parallel to `vm.codex.enemies` — see `EnemyDisclosure`. */
  private readonly enemyFull: EnemyDisclosure[] = [];

  private readonly favWeapons = new Set<string>();
  private readonly favModules = new Set<string>();

  /**
   * The key caps the HUD prints, resolved from the table the game is being
   * played on — see `setBindings` (#134).
   */
  private keyLabels: ReadonlyMap<string, string> = DEFAULT_KEY_LABELS;
  /** The `Input.bindingSerial()` `keyLabels` was built from; -1 = the defaults. */
  private bindSerial = -1;

  /** One row per held instance, keyed by instance id — see `instanceVM`. */
  private readonly instanceVMs = new Map<string, WeaponVM>();

  /**
   * The live instances behind those rows, same keys, pruned together.
   *
   * A loadout snapshot has to record the *modules* a hand was carrying (#17),
   * and `WeaponVM.slots` is a lossy place to read them from: `placeModules`
   * drops a module the weapon has no socket of that kind for, so a snapshot
   * taken off the view model would quietly save fewer modules than the weapon
   * actually holds. The instance is the record of truth, and holding it costs
   * one map entry per held weapon.
   */
  private readonly heldInstances = new Map<string, WeaponInstance>();

  /**
   * Display names captured with each loadout snapshot, parallel to
   * `LoadoutSlotVM.weaponIds`. Resolved at save time because by restore time the
   * instance may be gone, and an instance id is not a sentence.
   */
  private readonly loadoutNames: string[][] = [];

  /** The owned subsets the panel actually renders. Mutated in place. */
  private readonly ownedWeaponVMs: WeaponVM[] = [];
  private readonly ownedModuleVMs: ModuleVM[] = [];
  /** Membership test for the same set — a loadout restore has to check it. */
  private readonly ownedModuleIds = new Set<string>();
  private readonly devCatalogue: boolean;

  /**
   * Encounter tracking for materials — the codex is "an emergent document that
   * fills in with what this player actually observed" (docs/03 §7), and it read
   * 83/83 from the first frame because nothing was ever gating it. Now nothing
   * is discovered until `markMaterialSeen`/`markMaterialSeenId` says so, which
   * is the same contract reactions and enemies already keep.
   */
  private readonly materialsSeen = new Set<string>();
  /** Material id → codex id, and an O(1) already-seen flag for the hot path. */
  private readonly matNameById: string[] = [];
  private readonly matSeenFlag: Uint8Array;
  /**
   * Codex id → display label. A different question from `matNameById`, which
   * goes from the numeric material id the sim uses; crafting and the pouch both
   * speak codex ids and neither has a number to look up. 86 entries, built once.
   */
  private readonly matLabelByName = new Map<string, string>();

  /** Top charge tier per weapon def, ms. 0 when the weapon does not charge. */
  private readonly chargeTopMs = new Map<string, number>();

  /** Longest value ever seen on each ability cooldown — see `trackCooldowns`. */
  private readonly cdPeak: Record<CooldownKey, number> = {
    breach: 0, blink: 0, jet: 0, phase: 0,
  };
  private blinkChargeMax = 0;

  /** Captured reaction thumbnails, `event` → one byte of material id per cell. */
  private readonly thumbs = new Map<string, Uint8Array>();
  private readonly thumbVMs = new Map<string, IndexedThumbVM>();
  /** Material id → packed 0xRRGGBB; -1 is transparent. Built once at boot. */
  private readonly thumbPalette: number[] = [];

  // -- volatile-section change detection. Every term is monotonic, so a plain
  //    sum is a sound revision counter and costs a handful of loads per frame.
  private lastMainId: string | null = null;
  private lastOffId: string | null = null;
  private lastOwnedSig = 1;
  private lastStashSig = 1;
  private lastCraftSig = 1;
  private favRev = 0;
  private lastFavRev = -1;
  private lastCodexSig = -1;
  /** null until the first refresh, so the opening pass always discloses. */
  private lastAutopsy: boolean | null = null;
  /**
   * Reaction-hint inputs, allocated once. `refreshCodex` walks the whole
   * reaction table and the context is pure input, so a fresh object and a fresh
   * set per row would be garbage for nothing. `seen` holds display *labels* —
   * see the comment at the call site for why.
   */
  private readonly hintSeen = new Set<string>();
  private readonly hintCtx: {
    seen: ReadonlySet<string>;
    discovered: boolean;
    rules?: readonly ReactionEntryVM[];
  } = { seen: this.hintSeen, discovered: false };
  private lastRunId = '';
  private lastProgSig = -1;
  private lastMapSig = -1;
  private lastMapRoom = '';
  private lastMapHazard: string | null = null;
  /** NaN so the opening pass always builds — 0 is a reachable signature. */
  private lastFactionSig = NaN;

  /**
   * Measured value of `wielder`'s private SWING_SPEED. See `calibrateSwing` —
   * the alternative is a second copy of the constant, which is precisely how
   * the recovery bar came to divide a phase-local timer by a raw node sum.
   */
  private swingScale = 0;
  private readonly swingSamples = [0, 0, 0, 0, 0, 0];
  private swingCursor = 0;
  private readonly phaseState = new Map<string, { phase: string; est: number }>();

  /** One resource struct per hand, refilled in place — see `fillHand`. */
  private readonly res: Record<Hand, HandResources> = {
    main: newHandResources(),
    off: newHandResources(),
  };

  /** Live detonation read, refilled once per frame by `scanPrimers`. */
  private armed = { main: false, off: false };
  private scanState = {
    px: 0, py: 0, r2: 0, self: -1,
    main: null as readonly PrimerKindId[] | null,
    off: null as readonly PrimerKindId[] | null,
    w: null as World | null,
  };
  private readonly scanFn: (i: number) => void;

  constructor(
    private weapons: ReadonlyMap<string, WeaponDef>,
    private modules: ReadonlyMap<string, ModuleDef>,
    sources: UIAdapterSources = {},
  ) {
    // Everything below reads the material tables; a caller that forgot to
    // register them would otherwise get a codex of undefined labels.
    registerMaterials();

    this.enemyReg = sources.enemies ?? new EnemyRegistry();
    this.prog = sources.progression ?? new Progression();
    this.devCatalogue = sources.devCatalogue ?? false;

    // The primer scan runs every frame; a closure allocated per frame inside
    // the 0.5 ms budget is exactly the kind of garbage this file avoids.
    this.scanFn = (i) => this.scanEntity(i);

    let maxMat = 0;
    for (const d of allMaterials()) if (d.id > maxMat) maxMat = d.id;
    this.matSeenFlag = new Uint8Array(maxMat + 1);
    for (const d of allMaterials()) {
      this.matNameById[d.id] = d.name;
      this.matLabelByName.set(d.name, d.label);
    }

    // One shared palette for every captured thumbnail: the byte in the bitmap
    // *is* the material id, so the two never need to travel together.
    for (let i = 0; i <= maxMat; i++) this.thumbPalette[i] = -1;
    for (const d of allMaterials()) {
      // VOID is the absence of matter and must stay transparent — painting it
      // black would make a thumbnail of an open cave read as a solid block.
      if (d.id === 0) continue;
      this.thumbPalette[d.id] = (d.color.r << 16) | (d.color.g << 8) | d.color.b;
    }

    for (const def of weapons.values()) {
      let top = 0;
      for (const t of def.chargeStates ?? []) if (t.holdMs > top) top = t.holdMs;
      if (top > 0) this.chargeTopMs.set(def.id, top);
    }

    const player: PlayerVM = {
      hp: 100, hpMax: 100, shield: 0, shieldMax: 0,
      dashCharges: 1, dashMax: 1, dashRecharge01: 1,
      hasFlightMeter: false, flightFuel01: 0,
      main: emptyHand('main', this.keyLabelFor('mainAttack')),
      off: emptyHand('off', this.keyLabelFor('offAttack')),
      rhythm: {
        value01: 0, tier: 0, tierMax: 4, nextHand: null,
        decaying: false, bonusText: '',
      },
      gather: {
        active: false, tier: -1, tierMax: GATHER_TIERS.length,
        hazard: '', hazardClose01: 0, hazardCount: 0,
      },
      statuses: [],
      abilities: [],
      abilityKey: this.keyLabelFor('ability'),
      scrip: 0,
      ash: 0,
    };

    for (const def of this.weapons.values()) {
      const vm = buildWeaponVM(def);
      this.weaponVMs.push(vm);
      this.weaponById.set(vm.id, vm);
    }
    for (const def of this.modules.values()) {
      const vm = buildModuleVM(def);
      this.moduleVMs.push(vm);
      this.moduleById.set(vm.id, vm);
    }

    const rules = sources.reactionRules ?? buildReactionRules();
    const materials = this.buildMaterials(rules);
    const reactions = this.buildReactions(rules);
    const enemies = this.enemyReg.defs.map(buildEnemyVM);
    // The unredacted rows, before Composition Autopsy is asked about. Captured
    // here because `redactEnemyEntry` empties the live arrays in place and the
    // passive can be bought at any point in a session — without a copy, hiding
    // the composition once would destroy it for good.
    for (const e of enemies) {
      this.enemyFull.push({
        composition: e.composition.slice(),
        weaknesses: e.weaknesses.slice(),
        resistances: e.resistances.slice(),
      });
    }

    this.vm = {
      player,
      inventory: {
        revision: 0,
        slots: new Array<string | null>(WEAPON_SLOTS).fill(null),
        hands: { main: null, off: null },
        // The owned arrays are mutated in place, never replaced: the panel
        // holds this object across frames and `LoadoutPanel.update` keys its
        // rebuild off `revision`, not off array identity.
        weapons: this.devCatalogue ? this.weaponVMs : this.ownedWeaponVMs,
        modules: this.devCatalogue ? this.moduleVMs : this.ownedModuleVMs,
        loadouts: Array.from({ length: LOADOUT_SLOTS }, (_, i) => ({
          index: i,
          name: 'LOADOUT ' + (i + 1),
          filled: false,
          summary: 'empty',
          weaponIds: [] as string[],
          moduleIds: [] as string[][],
          moduleCount: 0,
          available: true,
          unavailableText: '',
        })),
        pairs: Array.from({ length: QUICK_PAIRS }, (_, i) => ({
          index: i,
          name: 'PAIR ' + (i + 1),
          mainId: null,
          offId: null,
          active: i === 0,
        })),
        // Mutated in place for the same reason the owned arrays are: the panel
        // holds this object across frames.
        stash: {
          items: [],
          capacity: 0,
          used: 0,
          free: 0,
          open: false,
          hint: STASH_NO_SLOTS,
        },
        crafting: {
          open: false,
          hint: CRAFT_NO_BENCH,
          recipes: [],
          pouch: [],
          pouchUsed: 0,
          pouchCap: 0,
        },
        atRestNode: false,
        catalogue: this.devCatalogue,
      },
      codex: {
        revision: 0,
        materials,
        reactions,
        enemies,
        totals: {
          materials: materials.length,
          reactions: this.reactionEvents.length,
          enemies: enemies.length,
        },
      },
      progression: {
        revision: 0,
        tracks: TRACK_IDS.map((id) => this.buildTrack(id)),
      },
      // Built on the first frame that carries a run — `refreshMap` fills it
      // from `run.rooms`, which is the only room graph that exists at runtime.
      map: null,
      // Built once and mutated in place. Unlike the map this is never null: a
      // profile that has done nothing still *has* standing with all six, and
      // "nothing has moved yet" is a readout the null case cannot give (#11).
      factions: buildFactionsVM(),
      vendor: null,
      // The shipped table until `setBindings` says otherwise — the same
      // fallback the HUD's own caps take, and for the same reason: an adapter
      // that has not been told yet still prints something true of a fresh
      // profile rather than nothing at all.
      keyLabels: this.keyLabels,
      paused: false,
    };

    this.refreshInventory(null, null, null, null);
    this.refreshCodex(null);
    this.refreshProgression();
    this.refreshFactions(null);
  }

  // ---------------------------------------------------------- construction

  private buildMaterials(rules: readonly ReactionRule[]): MaterialVM[] {
    const out: MaterialVM[] = [];
    for (const d of allMaterials()) {
      // VOID is the absence of matter; it has no codex entry to earn.
      if (d.id === 0) continue;
      const vm: MaterialVM = {
        id: d.name,
        name: d.label,
        category: CATEGORY_NAMES[d.category],
        tags: tagNamesOf(d.tags).map((t) => t.toLowerCase()),
        color: cssColor(d),
        // Earned, never granted. `refreshCodex` flips this from `materialsSeen`.
        discovered: false,
        props: materialProps(d),
        knownReactionIds: [],
      };
      if (d.desc) vm.note = d.desc;
      out.push(vm);
    }

    // Cross-link: a material's entry lists the reactions it can take part in,
    // resolved through tags as well as concrete ids — a tag rule is exactly a
    // reaction the material inherits for free (docs/03 §5.1).
    const byName = new Map(out.map((m) => [m.id, m]));
    for (const r of rules) {
      if (!r.event) continue;
      const id = r.event;
      for (const d of allMaterials()) {
        if (d.id === 0) continue;
        const hitsA = r.aIsTag ? (d.tags & r.a) !== 0 : d.id === r.a;
        const hitsB = r.bIsTag ? (d.tags & r.b) !== 0 : d.id === r.b;
        if (!hitsA && !hitsB) continue;
        const m = byName.get(d.name);
        if (m && !m.knownReactionIds.includes(id)) m.knownReactionIds.push(id);
      }
    }
    return out;
  }

  /**
   * Only rules carrying an `event` become codex entries. `event` is defined as
   * the codex discovery id, so a rule without one is engine mechanics the
   * player can never be credited for — listing it would produce a row that is
   * permanently redacted, which reads as a promise the game cannot keep.
   */
  private buildReactions(rules: readonly ReactionRule[]): ReactionEntryVM[] {
    const out: ReactionEntryVM[] = [];
    const seen = new Set<string>();
    for (const r of rules) {
      if (!r.event || seen.has(r.event)) continue;
      seen.add(r.event);
      const a = sideLabel(r.a, r.aIsTag);
      const b = sideLabel(r.b, r.bIsTag);
      out.push({
        id: r.event,
        a,
        b,
        condition: reactionCondition(r),
        outcome: reactionOutcome(r, a, b),
        event: r.event,
        discoveredAt: 0,
        seenCount: 0,
        swatch: reactionSwatch(r),
        discovered: false,
        // Filled by `refreshCodex` from the Reaction Hints passive.
        hint: '',
      });
      this.reactionEvents.push(r.event);
    }
    return out;
  }

  private buildTrack(id: TrackId): TrackVM {
    const def = TRACKS[id];
    const unlocks = unlocksForTrack(id)
      .slice()
      .sort((a, b) => a.cost - b.cost);
    return {
      id,
      name: def.name,
      fedBy: def.fedBy,
      glyph: TRACK_GLYPH[id],
      value: 0,
      next: 0,
      level: 0,
      unlocks: unlocks.map((u) => ({
        name: u.name,
        at: u.cost,
        unlocked: false,
        desc: u.desc,
      })),
    };
  }

  // ---------------------------------------------------------------- public

  /**
   * Favourites have no home in the save yet, so the adapter owns them. Wire
   * `UICallbacks.onToggleFavourite` straight to this and the star works.
   */
  toggleFavourite(kind: 'weapon' | 'module', id: string): void {
    // The panel hands back whatever key its row carried, which for a weapon is
    // an instance. Favourites stay *definition*-scoped: starring a roll and
    // finding a better one of the same weapon should not unstar the type.
    const key = kind === 'weapon' ? (this.instanceVMs.get(id)?.id ?? id) : id;
    const set = kind === 'weapon' ? this.favWeapons : this.favModules;
    if (set.has(key)) set.delete(key);
    else set.add(key);
    this.favRev++;
  }

  /** A row by either key: instance first, then definition (catalogue rows). */
  private weaponRow(id: string): WeaponVM | undefined {
    return this.instanceVMs.get(id) ?? this.weaponById.get(id);
  }

  /**
   * The codex's recorded-material set, by codex id.
   *
   * Exposed because crafting gates on it (#8): a recipe naming a material this
   * profile has never touched is withheld rather than shown as unaffordable,
   * and `craftingVM` wants exactly this set. The adapter is where discovery is
   * actually recorded, so handing out a read-only view is cheaper and less
   * fragile than a second copy on the run.
   */
  seenMaterials(): ReadonlySet<string> {
    return this.materialsSeen;
  }

  /** Records a material sighting by codex id (`MaterialDef.name`). */
  markMaterialSeen(name: string): void {
    if (this.materialsSeen.has(name)) return;
    this.materialsSeen.add(name);
    this.lastCodexSig = -1;
  }

  /**
   * Same, by material id. This is the one on the hot path — contact sampling
   * runs per cell, so the already-seen test has to be an array load, not a set
   * probe on a string that had to be looked up first.
   */
  markMaterialSeenId(mat: number): void {
    if (mat <= 0 || mat >= this.matSeenFlag.length || this.matSeenFlag[mat] === 1) return;
    this.matSeenFlag[mat] = 1;
    const name = this.matNameById[mat];
    if (name === undefined) return;
    this.materialsSeen.add(name);
    this.lastCodexSig = -1;
  }

  /**
   * Snapshots the equipped pair into a loadout slot.
   *
   * Hand position is preserved as an empty string rather than dropped, so
   * `restoreLoadout` can put a weapon back in the hand it came from. Squashing
   * the pair into a dense list is what made a one-handed loadout restore into
   * the wrong hand.
   */
  saveLoadout(index: number, name?: string): void {
    const inv = this.vm.inventory;
    const slot = inv.loadouts[index];
    if (!slot) return;
    const ids = [inv.hands.main ?? '', inv.hands.off ?? ''];
    // docs/04 §7 asks a loadout to snapshot "weapons + modules", and only the
    // weapon ids were stored — so saving, respeccing and restoring handed back
    // the same two weapons wearing whatever they happened to be wearing now.
    // The module set is read off the live instance rather than off the row's
    // `slots`: `placeModules` silently drops a module the weapon has no socket
    // of that kind for, and a snapshot must record what is installed, not what
    // the panel managed to draw.
    const mods = ids.map((id) => (id ? [...(this.heldInstances.get(id)?.modules ?? [])] : []));
    // Names are captured here rather than looked up on restore, because by then
    // the instance may be gone — and "b7f2… is no longer held" names nothing.
    const names = ids.map((id) => (id ? (this.weaponRow(id)?.name ?? id) : ''));

    slot.name = name ?? slot.name;
    slot.weaponIds = ids;
    slot.moduleIds = mods;
    slot.moduleCount = mods[0].length + mods[1].length;
    slot.filled = ids.some((x) => x !== '');
    this.loadoutNames[index] = names;
    slot.summary = slot.filled
      ? names.map((n) => n || '—').join(' + ') +
        ' · ' +
        slot.moduleCount +
        (slot.moduleCount === 1 ? ' module' : ' modules')
      : 'empty';
    slot.savedAt = Date.now();
    slot.available = true;
    slot.unavailableText = '';
    inv.revision++;
  }

  clearLoadout(index: number): void {
    const slot = this.vm.inventory.loadouts[index];
    if (!slot) return;
    slot.weaponIds = [];
    slot.moduleIds = [];
    slot.moduleCount = 0;
    slot.filled = false;
    slot.summary = 'empty';
    slot.available = true;
    slot.unavailableText = '';
    this.loadoutNames[index] = [];
    delete slot.savedAt;
    this.vm.inventory.revision++;
  }

  /**
   * What `onRestoreLoadout` should put back, or null when the slot is empty.
   *
   * The adapter owns the snapshot; equipping and installing are combat's
   * business, so this reports rather than acts. Anything the profile no longer
   * holds is dropped from the answer *and named in `missing`* — a restore that
   * quietly returns three of a build's seven modules is precisely the silent
   * wrong build this issue is about (#17).
   */
  restoreLoadout(index: number): LoadoutRestore | null {
    return this.resolveLoadout(index);
  }

  /** The snapshot resolved against the current holdings. See `restoreLoadout`. */
  private resolveLoadout(index: number): LoadoutRestore | null {
    const slot = this.vm.inventory.loadouts[index];
    if (!slot || !slot.filled) return null;
    const names = this.loadoutNames[index] ?? [];
    const missing: string[] = [];
    const ids: Array<string | null> = [null, null];
    const mods: string[][] = [[], []];

    for (let i = 0; i < 2; i++) {
      const id = slot.weaponIds[i] ?? '';
      if (!id) continue;
      if (!this.heldInstances.has(id)) {
        missing.push(names[i] || id);
        continue;
      }
      ids[i] = id;
      for (const m of slot.moduleIds[i] ?? []) {
        if (this.ownedModuleIds.has(m)) mods[i].push(m);
        else missing.push(this.moduleById.get(m)?.name ?? m);
      }
    }

    return {
      mainId: ids[0],
      offId: ids[1],
      mainModules: mods[0],
      offModules: mods[1],
      missing,
      exact: missing.length === 0,
    };
  }

  selectPair(index: number): void {
    const inv = this.vm.inventory;
    for (const p of inv.pairs) p.active = p.index === index;
    inv.revision++;
  }

  /**
   * Store the equipped hands as quick pair `index` (docs/04 §7). Without this
   * the two pairs are structurally empty forever, which is why both rows read
   * "PAIR n — — + —" and the radial hub named a pair that held nothing.
   */
  savePair(index: number): void {
    const inv = this.vm.inventory;
    const p = inv.pairs[index];
    if (!p) return;
    p.mainId = inv.hands.main;
    p.offId = inv.hands.off;
    inv.revision++;
  }

  /** What `onSelectPair` should equip. Null when that pair was never stored. */
  pair(index: number): { mainId: string | null; offId: string | null } | null {
    const p = this.vm.inventory.pairs[index];
    if (!p || (!p.mainId && !p.offId)) return null;
    return { mainId: p.mainId, offId: p.offId };
  }

  /**
   * docs/04 §7.1 for one weapon, against the live inventory. The loadout panel
   * derives this itself; exposing it here is what lets a test prove the VM
   * carries enough to produce real lines rather than empty ones.
   */
  synergy(weaponId: string): SynergyHintVM | null {
    const w = this.weaponRow(weaponId);
    return w ? computeSynergy(w, this.vm.inventory) : null;
  }

  /**
   * The seam #134 asked for: the live binding table, on its way to the key caps.
   *
   * The HUD prints four key labels — the two hand cards, the ability strip's
   * cap, and the ability tooltips — and until this existed all four resolved
   * through `DEFAULT_BINDINGS`. #129 had already removed the literals, which
   * made the strip follow the *shipped* table; a player who moved a key in
   * Settings was still told the shipped one. The overrides never reached here
   * because nothing carried them: they live on the profile, `main.ts` lays them
   * over `Game`'s `Input`, and `Game` is the only object holding both that
   * `Input` and this adapter. So `Game` hands the table across, once a frame.
   *
   * `serial` is `Input.bindingSerial()` — the table is mutated in place, so
   * identity cannot say whether it moved. Unchanged serial, no work: this is on
   * the per-frame path and `bindingRows` is a scan per rebindable action.
   *
   * Resolving through `bindingRows` rather than reading codes out of the table
   * directly is deliberate. It is the same function the settings screen, the
   * title legend and the in-run hint line print from, so a cap in the HUD and a
   * row in Settings cannot describe the same key differently — including the
   * awkward cases, like an action left with no key at all.
   */
  setBindings(table: Readonly<Record<string, string>>, serial: number): void {
    if (serial === this.bindSerial) return;
    this.bindSerial = serial;
    this.keyLabels = new Map(bindingRows(table, {}).map((r) => [r.action, r.key]));
    // #135. The HUD's caps are fields, filled just below; the prologue's are
    // not — its prompts name an action and the overlay prints the cap, because
    // the beat script is authored in `ui/prologue.ts` and never passes through
    // here. Handing the whole map across is what lets a surface outside the HUD
    // resolve a key without inventing a fifth speller for it.
    this.vm.keyLabels = this.keyLabels;
    // The three caps that are not per-frame data. A key cap changes when the
    // bindings change and at no other time, so this is their only writer —
    // `fillHand` deliberately does not touch `keyHint`, both because it is
    // skipped entirely when there is no wielder (the prologue) and because a
    // field with two writers is how #133 happened one file over.
    this.vm.player.abilityKey = this.keyLabelFor('ability');
    this.vm.player.main.keyHint = this.keyLabelFor('mainAttack');
    this.vm.player.off.keyHint = this.keyLabelFor('offAttack');
  }

  /**
   * What a key cap says for an action. `null` is "this verb has no button" —
   * Cling, which happens on wall contact — and is a different statement from a
   * binding we could not resolve, which the table itself reports as `unbound`.
   */
  private keyLabelFor(action: string | null): string {
    return action === null ? NO_KEY : (this.keyLabels.get(action) ?? NO_KEY);
  }

  /** Rebuild the per-frame view. Called once per rendered frame, not per tick. */
  update(
    w: World,
    playerIdx: number,
    mover: MoverState,
    wielder: WielderState | null,
    paused: boolean,
    frame: UIFrameState = {},
  ): GameVM {
    const p = this.vm.player;
    p.hp = w.hp[playerIdx];
    p.hpMax = w.maxHp[playerIdx];

    // Dash charges: ground charge plus air charges, so the HUD shows what the
    // player can actually spend rather than an abstract cooldown.
    p.dashMax = 1 + mover.tuning.maxAirDashes;
    p.dashCharges = mover.dashesLeft + mover.airDashesLeft;
    p.dashRecharge01 =
      mover.dashCooldown > 0
        ? 1 - mover.dashCooldown / (mover.tuning.dashDuration + mover.tuning.dashCooldown)
        : 1;

    // Issue #171. `'double-jump'` bodies have no meter to show at all — not
    // an empty one — the same "the field keeps its footprint but shows
    // nothing" distinction `hasFlightMeter` exists to draw for the HUD.
    const flightCap = flightCapacity(mover.tuning);
    p.hasFlightMeter = flightCap > 0;
    p.flightFuel01 = flightCap > 0 ? mover.flightFuel / flightCap : 0;

    if (wielder) {
      this.calibrateSwing(wielder);
      this.scanPrimers(w, playerIdx, wielder);
      this.fillHand(p.main, wielder, 'main');
      this.fillHand(p.off, wielder, 'off');
      this.fillRhythm(p.rhythm, wielder);
    }

    this.fillStatuses(p, w, playerIdx);
    this.fillAbilities(p, mover);
    this.fillGather(p, frame.gather);

    // The run purse. Zero without a run rather than the last run's balance —
    // scrip is lost on death (docs/05 §4) and a stale number would be the HUD
    // telling the player they still have what the death just took.
    const purse = frame.run?.inventory;
    p.scrip = purse?.scrip ?? 0;
    p.ash = purse?.ash ?? 0;

    // Walking into a rest node opens the stash (#6), and walking out shuts it.
    // One compare per frame; the redraw behind it fires on the two frames that
    // cross the threshold. `StashVM.open` moves with it independently — meta
    // owns that rule and passes the answer in — but the header says "REST NODE"
    // whether or not a stash view was supplied, so the flag earns its own bump.
    const atRest = frame.atRestNode ?? false;
    if (this.vm.inventory.atRestNode !== atRest) {
      this.vm.inventory.atRestNode = atRest;
      this.vm.inventory.revision++;
    }

    // The reference sections are refreshed only when their own monotonic
    // signature moves. Each check below is a few loads and a compare; the
    // walks behind them happen on equip, discovery and unlock.
    this.refreshInventory(
      wielder,
      frame.owned ?? null,
      frame.stash ?? null,
      frame.crafting ?? null,
    );
    this.refreshCodex(frame.run ?? null);
    this.refreshProgression();
    this.refreshMap(frame.run ?? null, frame.biomeHazard ?? null, frame.roomKind);
    this.refreshFactions(frame.run ?? null);
    this.refreshVendor(frame.run ?? null, frame.vendor ?? null);

    this.vm.paused = paused;
    return this.vm;
  }

  // ------------------------------------------------------- volatile refresh

  private refreshInventory(
    wielder: WielderState | null,
    owned: UIOwned | null,
    stash: UIStashView | null,
    crafting: UICraftingView | null,
  ): void {
    const mainInst = wielder?.main.instance ?? null;
    const offInst = wielder?.off.instance ?? null;
    const mainId = mainInst?.id ?? null;
    const offId = offInst?.id ?? null;
    const ownedSig = ownedSignature(owned);
    const stashSig = stashSignature(stash);
    const craftSig = craftingSignature(crafting);
    if (
      mainId === this.lastMainId &&
      offId === this.lastOffId &&
      this.favRev === this.lastFavRev &&
      ownedSig === this.lastOwnedSig &&
      stashSig === this.lastStashSig &&
      craftSig === this.lastCraftSig
    ) {
      return;
    }
    this.lastMainId = mainId;
    this.lastOffId = offId;
    this.lastFavRev = this.favRev;
    this.lastOwnedSig = ownedSig;
    this.lastStashSig = stashSig;
    this.lastCraftSig = craftSig;

    const inv = this.vm.inventory;
    inv.hands.main = mainId;
    inv.hands.off = offId;

    // Hands first, then everything else the run holds, in the order the run
    // picked it up. Slots 0 and 1 *are* the hands — `slots.includes(id)` is
    // what the panel calls "equipped" — and slots 2..7 are the stowed set that
    // used to be permanently null, which is why six rows of "empty" sat under
    // a header advertising eight weapon slots.
    //
    // One row per *instance*. Deduping by definition is what collapsed two
    // rolls of the same weapon into one row, and it is also why their module
    // sockets wrote over each other: the shared VM owned one `slots` array.
    this.ownedWeaponVMs.length = 0;
    const seen = new Set<string>();
    const take = (inst: WeaponInstance | null | undefined): void => {
      if (!inst || seen.has(inst.id)) return;
      const vm = this.instanceVM(inst);
      if (!vm) return;
      seen.add(inst.id);
      this.heldInstances.set(inst.id, inst);
      this.ownedWeaponVMs.push(vm);
    };
    take(mainInst);
    take(offInst);
    if (owned) for (const inst of owned.weapons) take(inst);

    // Instances the run no longer holds must not keep their VM alive: the maps
    // are the only strong reference and a run that loots for an hour would
    // otherwise accumulate one row per weapon it ever touched.
    for (const id of this.instanceVMs.keys()) if (!seen.has(id)) this.instanceVMs.delete(id);
    for (const id of this.heldInstances.keys()) if (!seen.has(id)) this.heldInstances.delete(id);

    for (let i = 0; i < WEAPON_SLOTS; i++) {
      inv.slots[i] = i < this.ownedWeaponVMs.length ? this.ownedWeaponVMs[i].instanceId : null;
    }
    this.refreshStash(inv, stash);
    this.refreshCrafting(inv, crafting);

    // Modules: everything held, installed or loose. Installed ones are reached
    // through the instances so a module never disappears from the list just
    // because it is currently in a socket — removal is free and never consumes
    // it (docs/04 §7), so it is owned either way.
    this.ownedModuleVMs.length = 0;
    this.ownedModuleIds.clear();
    const takeMod = (id: string): void => {
      if (this.ownedModuleIds.has(id)) return;
      const vm = this.moduleById.get(id);
      if (!vm) return;
      this.ownedModuleIds.add(id);
      this.ownedModuleVMs.push(vm);
    };
    // `heldInstances` is hands, then carried, then stash, in that order — a
    // stashed weapon keeps its build, so its modules are owned and its sockets
    // render filled. Walking the one map is also what stops the three sources
    // from drifting apart, which is how the stash would otherwise have arrived
    // with rows whose sockets all read "empty".
    for (const inst of this.heldInstances.values()) for (const id of inst.modules) takeMod(id);
    if (owned) for (const id of owned.modules) takeMod(id);

    for (const m of this.moduleVMs) m.installedOn = null;
    for (const wv of this.ownedWeaponVMs) for (const s of wv.slots) s.moduleId = null;

    for (const inst of this.heldInstances.values()) this.placeModules(inst);

    for (const wv of this.weaponVMs) wv.favourite = this.favWeapons.has(wv.id);
    for (const wv of this.ownedWeaponVMs) wv.favourite = this.favWeapons.has(wv.id);
    for (const m of this.moduleVMs) m.favourite = this.favModules.has(m.id);

    // Snapshots name instances, so what a snapshot resolves to changes whenever
    // the holdings do — which is exactly here, and never per frame.
    this.refreshLoadouts();

    inv.revision++;
  }

  /**
   * The stash section of the inventory VM (#5).
   *
   * A pure projection of what meta hands over: the capacity rule, the rest-node
   * gate and the sentence that explains a shut store all belong to
   * `meta/stash.ts` and are copied here, never recomputed. What this adds is the
   * catalogue read — a `StashItem` carries a definition id and a label, and the
   * panel also wants the rarity colour and the class glyph so a stash row looks
   * like the same object the inventory row does. The captured `label` still wins
   * over the catalogue name, because it is what the player saw when they put the
   * thing away.
   *
   * With no view supplied the store reads as owning no slots. That is the truth
   * for a profile that has bought none, and it is deliberately not "unlimited":
   * three Ash unlocks exist to sell these slots, and a stash that is free and
   * infinite makes all three unbuyable in effect.
   */
  private refreshStash(inv: InventoryVM, view: UIStashView | null): void {
    const st = inv.stash;
    st.capacity = view?.capacity ?? 0;
    st.used = view?.used ?? 0;
    st.free = Math.max(0, st.capacity - st.used);
    st.open = view?.open ?? false;
    st.hint = view?.hint ?? STASH_NO_SLOTS;

    st.items.length = 0;
    for (const it of view?.items ?? []) {
      const weapon = it.kind === 'weapon';
      const def = weapon ? this.weaponById.get(it.defId) : this.moduleById.get(it.defId);
      st.items.push({
        id: it.id,
        defId: it.defId,
        kind: it.kind,
        name: it.label || def?.name || it.defId,
        rarity: def?.rarity ?? 'common',
        glyph: def?.glyph ?? (weapon ? '◆' : '◈'),
        // Names, not ids: the row's whole job is to say what comes back with
        // the item, and "mod.frost.core.2" says it to nobody.
        moduleNames: it.modules.map((m) => this.moduleById.get(m)?.name ?? m),
      });
    }
  }

  /**
   * The workbench section of the inventory VM (#8).
   *
   * Everything except the labels is meta's answer copied across. The labels are
   * the one thing this file adds and the one thing meta cannot: a pouch line
   * reading "MOLTEN_IRON 40" names a constant, and the panel has to print the
   * word the codex uses for that matter.
   */
  private refreshCrafting(inv: InventoryVM, view: UICraftingView | null): void {
    const c = inv.crafting;
    c.open = view?.open ?? false;
    c.hint = view?.hint ?? CRAFT_NO_BENCH;
    c.pouchUsed = view?.pouchUsed ?? 0;
    c.pouchCap = view?.pouchCap ?? 0;

    c.pouch.length = 0;
    for (const p of view?.pouch ?? []) {
      c.pouch.push({ material: p.material, label: this.matLabel(p.material), units: p.units });
    }

    c.recipes.length = 0;
    for (const r of view?.recipes ?? []) {
      c.recipes.push({
        id: r.id,
        name: r.name,
        desc: r.desc,
        tier: r.tier,
        verb: r.verb,
        // The full input list, `have` beside `need`: the shortfall is a fact
        // about a cost and the player prices the dig off it.
        inputs: r.inputs.map((i) => ({
          material: i.material,
          label: this.matLabel(i.material),
          need: i.need,
          have: i.have,
        })),
        craftable: r.craftable,
        needs: r.needs,
        hint: r.hint,
      });
    }
  }

  /** Codex id → the word the codex prints. See `matLabelByName`. */
  private matLabel(name: string): string {
    return this.matLabelByName.get(name) ?? name;
  }

  /**
   * Re-resolve every snapshot against the current holdings (#17).
   *
   * A saved loadout names instances, and an instance can leave — stowed at a
   * rest node, dropped, or simply belonging to a run that ended. The panel
   * refuses the LOAD rather than restoring half a build silently, so it needs
   * the answer before the click, not after it.
   */
  private refreshLoadouts(): void {
    for (const slot of this.vm.inventory.loadouts) {
      if (!slot.filled) {
        slot.available = true;
        slot.unavailableText = '';
        continue;
      }
      const missing = this.resolveLoadout(slot.index)?.missing ?? [];
      slot.available = missing.length === 0;
      slot.unavailableText =
        missing.length === 0
          ? ''
          : missing.join(', ') + (missing.length === 1 ? ' is' : ' are') + ' no longer held';
    }
  }

  /**
   * The row for one holding, cloned off its definition's catalogue row.
   *
   * `slots` is deep-copied because module placement writes into it: two
   * instances sharing one array means installing on either shows on both.
   * Everything else is definition-derived and immutable, so a shallow copy is
   * both correct and cheap — this runs on equip and on loot, not per frame.
   */
  private instanceVM(inst: WeaponInstance): WeaponVM | null {
    const cached = this.instanceVMs.get(inst.id);
    if (cached) return cached;
    const base = this.weaponById.get(inst.defId);
    if (!base) return null;
    const vm: WeaponVM = {
      ...base,
      instanceId: inst.id,
      slots: base.slots.map((s) => ({ ...s })),
    };
    this.instanceVMs.set(inst.id, vm);
    return vm;
  }

  private placeModules(inst: WeaponInstance | null): void {
    if (!inst) return;
    const wv = this.instanceVMs.get(inst.id);
    if (!wv) return;
    for (const id of inst.modules) {
      const m = this.moduleById.get(id);
      if (!m) continue;
      const slot = targetSlotFor(m, wv);
      if (slot) slot.moduleId = id;
      // The instance, not the definition: "installed on" is a property of one
      // roll, and naming the definition puts the module in every copy at once.
      m.installedOn = inst.id;
    }
  }

  private refreshCodex(run: RunState | null): void {
    const st = this.prog.state;
    // Every term only ever grows within a run, so the sum is a valid revision;
    // the run id catches the one discontinuity, which is a new run resetting
    // the per-run halves to zero.
    const sig =
      st.codexSeen.length * 131 +
      st.lifetime.deaths * 17 +
      (run ? run.discoveries.length * 7 + run.stats.kills : 0) +
      this.materialsSeen.size +
      // Composition Autopsy is an unlock, and buying it changes what every
      // enemy row is allowed to say without changing a single discovery.
      st.unlocked.length * 1531;
    const runId = run?.runId ?? '';
    if (sig === this.lastCodexSig && runId === this.lastRunId) return;
    this.lastCodexSig = sig;
    this.lastRunId = runId;

    const cx = this.vm.codex;

    // Passives decide what the codex may say, so they are resolved before
    // anything is written rather than after — reactions and enemies both ask.
    const passives = resolvePassives(this.prog);

    // Reaction hints are matched against the strings the rows actually print,
    // and `ReactionEntryVM.a` is a *label* while `materialsSeen` holds codex
    // ids. Reconciling the two here is what keeps `reactionHint` honest: a
    // "missing reagent" line that names a material the player has in fact
    // recorded is worse than no hint at all. A tag side has no single label and
    // is simply never in the set, which reads as unrecorded — the conservative
    // answer, and `reactionHint` says nothing when neither side is known.
    const seenLabels = this.hintSeen;
    if (passives.hintLevel > 0) {
      seenLabels.clear();
      for (const m of cx.materials) if (this.materialsSeen.has(m.id)) seenLabels.add(m.name);
      this.hintCtx.rules = cx.reactions;
    }

    let order = 0;
    for (let i = 0; i < cx.reactions.length; i++) {
      const entry = cx.reactions[i];
      const id = this.reactionEvents[i];
      const lifetime = st.codexSeen.includes(id);
      const thisRun = run ? run.discoveries.includes(id) : false;
      entry.discovered = lifetime || thisRun;
      // The codex is ordered by when you found things, and undiscovered rows
      // have to trail them without ever reshuffling — so unknowns sort by
      // their stable table position, offset past any possible discovery.
      entry.discoveredAt = entry.discovered ? ++order : 1e6 + i;
      // No repeat counter exists anywhere; a discovery is recorded once. This
      // is 1 for "observed", not a tally.
      entry.seenCount = entry.discovered ? 1 : 0;
      // The capture, if this run (or an earlier one) photographed it. Absent is
      // normal — `codex.ts` falls back to the three-band material swatch. The
      // store only ever grows, so a row is never un-assigned: `delete` here
      // would drop every entry into dictionary mode for no gain.
      const shot = this.thumbVMs.get(id);
      if (shot) entry.thumb = shot;

      // The Reaction Hints passive (`codex.hint.*`). One shared context object
      // rather than one per row: this loop runs over the whole reaction table
      // and the ctx is pure input.
      if (passives.hintLevel === 0) {
        entry.hint = '';
      } else {
        this.hintCtx.discovered = entry.discovered;
        entry.hint = reactionHint(entry, this.hintCtx, passives) ?? '';
      }
    }

    const seen = this.materialsSeen;
    for (const m of cx.materials) m.discovered = seen.has(m.id);

    // What an enemy row is allowed to disclose. Recomputed only when the
    // passive itself moves: it is a full rewrite of three arrays per enemy, and
    // it changes at most a handful of times in a profile's life.
    const autopsy = passives.showComposition;
    const disclose = autopsy !== this.lastAutopsy;
    if (disclose) this.lastAutopsy = autopsy;

    for (let k = 0; k < cx.enemies.length; k++) {
      const e = cx.enemies[k];
      e.killCount = run ? (run.stats.killsBy[e.id] ?? 0) : 0;
      e.deathsTo = st.deathsBy[e.id] ?? 0;
      e.discovered = e.killCount > 0 || e.deathsTo > 0;
      if (disclose) this.applyDisclosure(e, this.enemyFull[k], passives);
    }

    cx.revision++;
  }

  /**
   * Composition Autopsy (`codex.autopsy`), applied to one enemy row.
   *
   * The full rows go back first and are then withheld again, because
   * `redactEnemyEntry` empties in place: without the restore, the first refresh
   * that hides a composition would delete it, and buying the passive later
   * would reveal three empty sections. The rule and the sentence both come from
   * `meta/passives.ts` — this file decides nothing about what may be shown, only
   * when to ask.
   */
  private applyDisclosure(
    e: EnemyEntryVM,
    full: EnemyDisclosure | undefined,
    p: RunPassives,
  ): void {
    if (!full) return;
    e.composition.length = 0;
    for (const c of full.composition) e.composition.push(c);
    e.weaknesses.length = 0;
    for (const w of full.weaknesses) e.weaknesses.push(w);
    e.resistances.length = 0;
    for (const w of full.resistances) e.resistances.push(w);

    e.redacted = redactEnemyEntry(e, p);
    // A blank section reads as missing data; a locked one reads as something to
    // go and earn, which is the only reason to put this behind a track at all.
    e.redactedHint = e.redacted ? AUTOPSY_LOCK_HINT : '';
  }

  private refreshProgression(): void {
    const st = this.prog.state;
    let sig = st.unlocked.length * 1_000_003;
    for (const id of TRACK_IDS) {
      const t = st.tracks[id];
      sig += t.earned * 31 + t.spent * 7;
    }
    if (sig === this.lastProgSig) return;
    this.lastProgSig = sig;

    const pv = this.vm.progression;
    for (const track of pv.tracks) {
      // Currencies report what is left to spend, meters their lifetime total —
      // `balance()` already encodes that distinction.
      track.value = this.prog.balance(track.id);
      track.level = 0;
      let next = track.value;
      for (const u of track.unlocks) {
        u.unlocked = this.prog.isUnlocked(unlockIdFor(track, u.name));
        if (u.unlocked) track.level++;
        else if (next <= track.value && u.at > track.value) next = u.at;
      }
      track.next = next;
    }
    pv.revision++;
  }

  /**
   * The minimap, from the run's room graph.
   *
   * Room ids are chunk coordinates (`"cx,cy"`, written by `Game.trackLocation`),
   * so a room is one lattice cell and the panel's own bounds pass does the
   * layout. Unvisited orthogonal neighbours of visited rooms are emitted too:
   * that is what the footer means by "the rest are inferred from exits", and a
   * map with no edge into the dark is a map that never invites you anywhere.
   */
  private refreshMap(
    run: RunState | null,
    hazard: string | null,
    roomKind?: (cx: number, cy: number) => string,
  ): void {
    if (!run) {
      if (this.vm.map !== null) this.vm.map = null;
      return;
    }
    const rooms = run.rooms;
    const sig = rooms.visited.size * 131 + rooms.edges.length;
    if (
      sig === this.lastMapSig &&
      rooms.current === this.lastMapRoom &&
      hazard === this.lastMapHazard &&
      this.vm.map
    ) {
      return;
    }
    this.lastMapSig = sig;
    this.lastMapRoom = rooms.current;
    this.lastMapHazard = hazard;

    const out: MapRoomVM[] = [];
    const placed = new Set<string>();
    const push = (id: string, visited: boolean): void => {
      if (placed.has(id)) return;
      const comma = id.indexOf(',');
      if (comma < 0) return;
      const x = Number(id.slice(0, comma));
      const y = Number(id.slice(comma + 1));
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      placed.add(id);
      out.push({
        id,
        x,
        y,
        w: 1,
        h: 1,
        visited,
        current: id === rooms.current,
        // Worldgen tags rooms now (#25), so this asks it rather than guessing.
        // `asMapRoomKind` is the gate: anything the map has no rule for becomes
        // `normal`, because inventing a room kind the generator never placed is
        // a lie the player acts on. `current` is a separate field — writing
        // 'current' here, as this used to, spent the one field that says what a
        // room *is* on a fact the row already carried.
        kind: roomKind ? asMapRoomKind(roomKind(x, y)) : 'normal',
        biome: run.currentBiome,
      });
    };

    for (const id of rooms.visited) push(id, true);
    for (const id of [...placed]) {
      const comma = id.indexOf(',');
      const x = Number(id.slice(0, comma));
      const y = Number(id.slice(comma + 1));
      for (const [dx, dy] of ROOM_NEIGHBOURS) push(`${x + dx},${y + dy}`, false);
    }

    const prev = this.vm.map;
    this.vm.map = {
      revision: (prev?.revision ?? 0) + 1,
      rooms: out,
      biomeName: run.currentBiome.replace(/[-_]/g, ' '),
      mode: run.worldMode,
      hazardText: hazard,
    } satisfies MapVM;
  }

  /**
   * Standing with all six factions, and the ledger of deeds behind it (#11).
   *
   * Read straight off `run.factions`, which is a `FactionState` every `RunState`
   * has already carried and serialised since the system was written — so this
   * needs nothing from the vendor work landing in parallel. The bars move on
   * every faction-tagged kill: `recordKill` applies the slain faction's deed
   * and `Game`'s death handler announces the band crossings (#70). The twelve
   * *non-kill* deeds in `DEEDS` still have no trigger, so nothing but a corpse
   * moves these numbers yet.
   *
   * Dirty-checked on the three axis numbers, which is the entire state: they are
   * the only thing reputation is stored as, and every derived value in the view
   * model is a pure function of them. The deed ledger is folded in as a count so
   * a deed that fires at zero weight still refreshes the list.
   */
  private refreshFactions(run: RunState | null): void {
    const fv = this.vm.factions;
    const st = run?.factions ?? null;
    const a = st ? st.axes : ZERO_AXES;
    let deedCount = 0;
    if (st) for (const k in st.deeds) deedCount += st.deeds[k as DeedId] ?? 0;
    // Three small integers and a count; distinct primes keep the signature from
    // colliding on a swap between axes.
    const sig = a.veil * 131 + a.substrate * 1009 + a.continuance * 40009 + deedCount * 7919;
    if (sig === this.lastFactionSig) return;
    this.lastFactionSig = sig;

    const f = new Factions(st ?? EMPTY_FACTION_STATE);
    let untouched = true;
    for (const axis of fv.axes) {
      fillFaction(axis.pos, f);
      fillFaction(axis.neg, f);
      axis.marker01 = repMarker01(axis.pos.reputation, REP_MAX);
      axis.untouched = axis.pos.reputation === 0;
      if (!axis.untouched) untouched = false;
    }
    fv.untouched = untouched;

    // Rebuilt rather than pooled: it changes only when a deed fires, which is a
    // handful of times per run, and the sort is over at most twelve entries.
    fv.deeds.length = 0;
    if (st) {
      for (const k in st.deeds) {
        const id = k as DeedId;
        const count = st.deeds[id] ?? 0;
        if (count <= 0) continue;
        const d = DEEDS[id];
        if (!d) continue;
        fv.deeds.push({
          id,
          name: d.name,
          factionName: FACTION_BY_ID[d.faction].name,
          count,
          weight: d.weight,
        });
      }
      // Biggest total swing first, by *magnitude*: the kill deeds carry negative
      // weight (killing one of theirs is the mirror of courting their opposite),
      // and sorting on the signed value would bury the thing most responsible
      // for a faction hating you at the bottom of the list.
      fv.deeds.sort((x, y) => Math.abs(y.count * y.weight) - Math.abs(x.count * x.weight));
    }
    fv.revision++;
  }

  /**
   * The vendor in range, with prices judged against the purse.
   *
   * Not dirty-checked against a previous frame: the whole thing is null on all
   * but a few seconds of a run, and while it is non-null the price of standing
   * still is one object and a short array — far cheaper than the signature it
   * would take to avoid them.
   */
  private refreshVendor(run: RunState | null, input: UIVendorInput | null): void {
    if (!input) {
      if (this.vm.vendor !== null) this.vm.vendor = null;
      return;
    }
    const scrip = run?.inventory.scrip ?? 0;
    const offers: VendorOfferVM[] = [];
    for (const o of input.offers) {
      const affordable = o.price <= scrip;
      offers.push({
        id: o.id,
        label: o.label,
        price: o.price,
        affordable,
        // A refusal the vendor already has outranks this one: "not for the likes
        // of you" is a better sentence than "you are 50 short", and it is the
        // true cause. Only a line with no other reason gets the price one.
        refusal: o.refusal ?? (affordable ? '' : `${o.price - scrip} scrip short`),
      });
    }
    // The counter holds one thing and a clock turns it over — the altar's
    // shape, which docs/13 §5 records as the best-received interaction in the
    // build. Falling back to the first takeable line keeps the panel honest for
    // an integrator that has not wired the rotation yet; falling back to nothing
    // would render an empty counter over a full shelf.
    const counterId = input.counterId ?? offers.find((o) => o.refusal === '')?.id ?? null;
    this.vm.vendor = {
      factionId: input.factionId,
      factionName: input.factionName,
      standing: input.standing,
      inRange: input.inRange,
      open: input.open,
      hostile: input.hostile ?? false,
      refusal: input.refusal,
      offers,
      counterId,
      rotateIn: input.rotateIn ?? 0,
      scrip,
    };
  }

  // ------------------------------------------------------------- HUD fills

  /**
   * Measure `wielder`'s private SWING_SPEED rather than copying it.
   *
   * Every node timing is divided by that constant before it is used, so the raw
   * sum of `windup + active + recovery` is not the duration of anything the
   * player sees — dividing a phase-local `timer` by it is what made the recovery
   * bar restart three times per swing and never reach 1. `timer` counts real ms
   * inside a phase whose real length is `unscaled / SWING_SPEED`, so
   * `unscaled / timer` is an upper bound on the constant and the tightest bound
   * within a phase is a good estimate of it. A short ring of recent per-phase
   * estimates is kept instead of an all-time minimum so the readout still
   * follows if combat retunes the constant.
   */
  private calibrateSwing(state: WielderState): void {
    this.calibrateHand('main', state.main, state);
    this.calibrateHand('off', state.off, state);
  }

  private calibrateHand(key: string, h: HandState, state: WielderState): void {
    let slot = this.phaseState.get(key);
    if (!slot) {
      slot = { phase: '', est: Infinity };
      this.phaseState.set(key, slot);
    }
    const phase = h.phase;
    if (phase !== slot.phase) {
      if (slot.est !== Infinity) {
        this.swingSamples[this.swingCursor] = slot.est;
        this.swingCursor = (this.swingCursor + 1) % this.swingSamples.length;
        let best = Infinity;
        for (const s of this.swingSamples) if (s > 0 && s < best) best = s;
        this.swingScale = best === Infinity ? 0 : best;
      }
      slot.phase = phase;
      slot.est = Infinity;
      return;
    }
    const unscaled = this.phaseUnscaledMs(h, state);
    if (unscaled <= 0 || h.timer <= 0) return;
    const est = unscaled / h.timer;
    if (est < slot.est) slot.est = est;
  }

  /** Duration of the phase this hand is in, before SWING_SPEED is applied. */
  private phaseUnscaledMs(h: HandState, state: WielderState): number {
    const node = h.node;
    const rw = h.weapon;
    if (!node || !rw) return 0;
    switch (h.phase) {
      case 'windup':
        return node.windup * rw.windupMul;
      case 'active':
        return node.active;
      case 'recovery':
        // Recovery is the one phase whose length moves with the rhythm meter,
        // and it moves live rather than being latched at node start.
        return node.recovery * rw.recoveryMul * (1 - state.rhythm * RHYTHM_RECOVERY_FRAC);
      default:
        return 0;
    }
  }

  /** Unscaled sum of the node the hand is running, and the part already done. */
  private nodeProgress(h: HandState, state: WielderState): { done: number; total: number } {
    const node = h.node;
    const rw = h.weapon;
    if (!node || !rw) return { done: 0, total: 0 };
    const wu = node.windup * rw.windupMul;
    const ac = node.active;
    const rc = node.recovery * rw.recoveryMul * (1 - state.rhythm * RHYTHM_RECOVERY_FRAC);
    const done = h.phase === 'active' ? wu : h.phase === 'recovery' ? wu + ac : 0;
    return { done, total: wu + ac + rc };
  }

  /**
   * Which hands could detonate a primer *right now*.
   *
   * The old read was `def.detonates` against the other hand's `def.primes` — a
   * property of the loadout, true before the fight started and true after it
   * ended. What the HUD light has to mean (docs/04 §1) is "the thing in front of
   * you is primed", so this asks the world. Terrain primers are not included:
   * they need the CellWorld, which the adapter deliberately does not hold.
   */
  private scanPrimers(w: World, playerIdx: number, state: WielderState): void {
    this.armed.main = false;
    this.armed.off = false;
    const md = state.main.weapon?.detonates ?? null;
    const od = state.off.weapon?.detonates ?? null;
    const mr = md && md.length > 0 ? (state.main.weapon?.reach ?? 0) + 12 : 0;
    const or = od && od.length > 0 ? (state.off.weapon?.reach ?? 0) + 12 : 0;
    const r = Math.max(mr, or);
    if (r <= 0) return;

    const s = this.scanState;
    s.w = w;
    s.px = w.x[playerIdx];
    s.py = w.y[playerIdx];
    s.r2 = r * r;
    s.self = playerIdx;
    s.main = mr > 0 ? md : null;
    s.off = or > 0 ? od : null;
    w.each(Comp.HEALTH, this.scanFn);
    s.w = null;
  }

  private scanEntity(i: number): void {
    const s = this.scanState;
    const w = s.w;
    if (!w || i === s.self) return;
    if (this.armed.main && this.armed.off) return;
    const dx = w.x[i] - s.px;
    const dy = w.y[i] - s.py;
    if (dx * dx + dy * dy > s.r2) return;
    if (!this.armed.main && s.main) {
      for (const p of s.main) {
        if (hasStatus(w, i, p)) {
          this.armed.main = true;
          break;
        }
      }
    }
    if (!this.armed.off && s.off) {
      for (const p of s.off) {
        if (hasStatus(w, i, p)) {
          this.armed.off = true;
          break;
        }
      }
    }
  }

  private fillHand(vm: HandVM, state: WielderState, hand: Hand): void {
    const h = hand === 'main' ? state.main : state.off;
    const rw = h.weapon;
    if (!rw || !h.instance) {
      vm.weaponId = null;
      vm.instanceId = null;
      vm.name = '—';
      vm.glyph = '·';
      vm.comboStep = -1;
      vm.comboLength = 0;
      vm.ready01 = 1;
      vm.ready = true;
      vm.charge01 = null;
      vm.heat01 = null;
      vm.overheated = false;
      vm.ammo = null;
      vm.ammoMax = null;
      vm.reloading = false;
      vm.reload01 = 1;
      vm.reserve01 = null;
      vm.reserveKind = '';
      vm.detonationArmed = false;
      return;
    }

    const def = this.weapons.get(h.instance.defId);
    vm.weaponId = h.instance.defId;
    vm.instanceId = h.instance.id;
    vm.name = def?.name ?? h.instance.defId;
    vm.glyph = def ? CLASS_GLYPH[def.class] : '◆';
    // Rarity comes from the definition. Reading it off the instance was a cast
    // over a field WeaponInstance has never had, so it always said "common".
    vm.rarity = def ? RARITY[def.rarity] : 'common';

    const node = h.node;
    vm.comboNodeName = node ? node.id : '';
    vm.comboLength = def ? def.combo.length : 0;
    vm.comboStep = node && def ? def.combo.findIndex((n) => n.id === node.id) : -1;

    // `ready01` is a recovery readout: 0 the instant an attack starts, 1 when
    // the hand can act again. `h.timer` restarts at every phase transition, so
    // the fraction has to be taken across the whole node — see `calibrateSwing`
    // for why the scale is measured rather than copied. Before the first
    // calibrated swing it degrades to phase boundaries: coarse, never wrong.
    if (h.phase === 'charging') {
      vm.ready01 = 0;
      vm.ready = false;
    } else if (node && h.phase !== 'idle') {
      const { done, total } = this.nodeProgress(h, state);
      const elapsed = this.swingScale > 0 ? h.timer * this.swingScale : 0;
      vm.ready01 = total > 0 ? Math.min(1, (done + elapsed) / total) : 1;
      vm.ready = false;
    } else {
      vm.ready01 = 1;
      vm.ready = true;
    }

    // Charge tiers are per weapon: the rail's top tier is 1300 ms, the bow's
    // 1200, the chain's 1400. A fixed 800 saturated the bar well before the
    // tier it was supposed to be counting toward actually landed.
    const topHold = this.chargeTopMs.get(h.instance.defId) ?? 0;
    vm.charge01 = topHold > 0 && h.chargeHeld > 0 ? Math.min(1, h.chargeHeld / topHold) : null;

    // Magazine, heat and reservoir all come from one read now that combat owns
    // them (#13). `readHandResources` fills a caller-owned struct, so the
    // per-frame path still allocates nothing — and it already folds the
    // module-`overheat` case, which is why the old `rw.risks` probe is gone.
    //
    // -1 is combat's sentinel for "this weapon has no such resource", and it
    // becomes null rather than 0: a magazine that reads 0/0 on a sword is worse
    // than no magazine row at all. That distinction is the whole reason the
    // previous hard-coded nulls were there.
    const res = readHandResources(state, hand, this.res[hand]);
    vm.ammo = res.ammo >= 0 ? res.ammo : null;
    vm.ammoMax = res.ammo >= 0 ? res.ammoMax : null;
    vm.heat01 = res.heat01 >= 0 ? res.heat01 : null;
    vm.overheated = res.overheated;
    vm.reloading = res.reloading;
    vm.reload01 = res.reload01;
    vm.reserve01 = res.charge01 >= 0 ? res.charge01 : null;
    vm.reserveKind = res.chargeKind;

    // "Armed" is a live read of a primer sitting on something in reach, not a
    // property of the pair — see `scanPrimers`.
    vm.detonationArmed = hand === 'main' ? this.armed.main : this.armed.off;
  }

  private fillRhythm(vm: RhythmVM, state: WielderState): void {
    vm.value01 = state.rhythm;
    vm.tierMax = 4;
    vm.tier = Math.floor(state.rhythm * 4);
    // The prompt is the point: a meter the player can't act on is decoration.
    vm.nextHand = state.lastHand === 'main' ? 'off' : state.lastHand === 'off' ? 'main' : null;
    // #133. This was `rhythm > 0 && lastHand === null`, which after the first
    // swing of a run is never true — `lastHand` follows the button pressed and
    // is never cleared, so the HUD could not show the meter draining at all.
    // Decay is a property of the meter, so it is asked of the meter.
    vm.decaying = rhythmDecaying(state);
    vm.bonusText = rhythmBonusText(state.rhythm, RHYTHM_DAMAGE_FRAC, RHYTHM_RECOVERY_FRAC);
  }

  private fillStatuses(p: PlayerVM, w: World, i: number): void {
    const out: StatusVM[] = [];
    const base = i * World.STATUS_SLOTS;
    for (let k = 0; k < World.STATUS_SLOTS; k++) {
      const kind = w.statusKind[base + k];
      if (kind === StatusKind.NONE) continue;
      let vm = this.statusPool[out.length];
      if (!vm) {
        vm = {} as StatusVM;
        this.statusPool.push(vm);
      }
      const name = STATUS_NAMES[kind] ?? 'status';
      vm.id = name.toLowerCase().replace(/\s+/g, '_');
      vm.name = name;
      vm.glyph = STATUS_GLYPH[kind] ?? '•';
      vm.stacks = w.statusStacks[base + k];
      // The ECS stores remaining seconds, not the original duration, so this
      // normalises against a nominal window. It only drives the depletion bar;
      // the number that matters (stacks) is exact.
      vm.remaining01 = Math.max(0, Math.min(1, w.statusTime[base + k] / 5));
      vm.kind = BENEFICIAL.has(kind) ? 'buff' : 'debuff';
      out.push(vm);
    }
    p.statuses = out;
  }

  /**
   * Cooldown lengths, measured rather than copied.
   *
   * `abilities.ts` keeps BREACH_COOLDOWN, BLINK_COOLDOWN, JET_COOLDOWN and
   * PHASE_COOLDOWN private, and the HUD divided the blink timer by a hard-coded
   * `0.9` — which is none of them, so the one cooldown that was surfaced
   * emptied at the wrong rate and could read negative. A cooldown is written
   * once at its full length and then only counts down, so the largest value
   * ever observed *is* the length. Same argument as `calibrateSwing`: one
   * constant, one owner, and the readout follows a retune for free.
   */
  private trackCooldowns(mover: MoverState): void {
    const p = this.cdPeak;
    if (mover.breachCooldown > p.breach) p.breach = mover.breachCooldown;
    if (mover.blinkCooldown > p.blink) p.blink = mover.blinkCooldown;
    if (mover.jetCooldown > p.jet) p.jet = mover.jetCooldown;
    if (mover.phaseCooldown > p.phase) p.phase = mover.phaseCooldown;
    if (mover.blinkCharges > this.blinkChargeMax) this.blinkChargeMax = mover.blinkCharges;
  }

  private cooldownLeft(mover: MoverState, key: CooldownKey): number {
    switch (key) {
      case 'breach': return mover.breachCooldown;
      case 'blink': return mover.blinkCooldown;
      case 'jet': return mover.jetCooldown;
      default: return mover.phaseCooldown;
    }
  }

  private fillAbilities(p: PlayerVM, mover: MoverState): void {
    this.trackCooldowns(mover);
    const out: AbilityVM[] = [];
    for (let k = 0; k < ABILITY_LIST.length; k++) {
      const a = ABILITY_LIST[k];
      const owned = (mover.abilities & a.bit) !== 0;
      // Unowned abilities still render, dimmed: the HUD is also the promise of
      // what is out there to find, which is a Metroidvania's main engine.
      let vm = this.abilityPool[k];
      if (!vm) {
        vm = {} as AbilityVM;
        this.abilityPool.push(vm);
      }
      vm.id = a.id;
      vm.name = a.name;
      vm.glyph = a.glyph;
      vm.keyHint = this.keyLabelFor(a.keyAction);
      vm.unlocked = owned;

      const left = a.cooldown ? this.cooldownLeft(mover, a.cooldown) : 0;
      const full = a.cooldown ? this.cdPeak[a.cooldown] : 0;
      vm.cooldown01 = left > 0 && full > 0 ? Math.max(0, 1 - left / full) : 1;

      vm.charges = a.charges ? mover.blinkCharges : null;
      vm.chargesMax = a.charges ? this.blinkChargeMax : null;

      // Spent charges block the press as surely as a running cooldown does, so
      // "ready" has to mean both. A chip that reads ready and then eats the
      // input is worse than one that reads spent.
      vm.ready = owned && left <= 0 && (vm.charges === null || vm.charges > 0);

      // The whole point of the one-button scheme: what *would* fire, not what
      // did. `mover.chosen` is recomputed every substep by `updateAbilities`.
      vm.armed = owned && a.chosen !== Chosen.NONE && mover.chosen === a.chosen;
      out.push(vm);
    }
    p.abilities = out;
  }

  /**
   * The gathering field's two readings (#122's data, #129's reader).
   *
   * The only derivation here is the one the seam deliberately left undone:
   * `hazardDist` arrives in cells and leaves as a fraction of the radius the
   * player chose by holding. That has to happen on this side because the radius
   * is on this side too, and computing it at the source would put the same
   * division in `Game`.
   *
   * `hazardDist` is `Infinity` when the scan found nothing, and the scan is
   * best-effort inside the field's per-tick work budget — so "no hazard this
   * tick" is a real answer and is rendered as no warning, never as a warning at
   * distance zero. A NaN or a negative would be the same mistake; both are
   * clamped rather than trusted.
   */
  private fillGather(p: PlayerVM, g: UIGatherInput | null | undefined): void {
    const vm = p.gather;
    vm.tierMax = GATHER_TIERS.length;
    if (!g || !g.active) {
      vm.active = false;
      vm.tier = -1;
      vm.hazard = '';
      vm.hazardClose01 = 0;
      vm.hazardCount = 0;
      return;
    }
    vm.active = true;
    vm.tier = Math.max(0, Math.min(GATHER_TIERS.length - 1, g.tier | 0));

    const named = g.hazardMat > 0 ? materialName(g.hazardMat) : undefined;
    const reachable = Number.isFinite(g.hazardDist) && g.radius > 0;
    if (!named || !reachable || g.hazardCount <= 0) {
      vm.hazard = '';
      vm.hazardClose01 = 0;
      vm.hazardCount = 0;
      return;
    }
    vm.hazard = named.toLowerCase().replace(/_/g, ' ');
    vm.hazardClose01 = Math.max(0, Math.min(1, 1 - g.hazardDist / g.radius));
    vm.hazardCount = g.hazardCount;
  }

  // ------------------------------------------------------ codex thumbnails

  /**
   * Photograph the cells around a reaction the first time it fires.
   *
   * docs/03 §7 asks for "the reaction as the player actually saw it", which is
   * what makes the codex a record of the run rather than a reference table.
   * `ReactionEngine.onDiscovery` already carries the contact coordinates, so
   * this is a `THUMB_DIM²` block of `peek` — 256 array reads, once per reaction
   * per profile, on a path that by definition runs at most `THUMB_CAP` times in
   * a lifetime. There is no encode and no allocation beyond the row itself.
   *
   * Returns false when nothing was stored, which is either "already have it" or
   * "the budget is full". Both are normal; neither is an error.
   */
  captureReactionThumb(cells: CellPeek, event: string, x: number, y: number): boolean {
    if (this.thumbs.has(event)) return false;
    if (this.thumbs.size >= THUMB_CAP) return false;

    const data = new Uint8Array(THUMB_DIM * THUMB_DIM);
    const x0 = Math.round(x) - (THUMB_DIM >> 1);
    const y0 = Math.round(y) - (THUMB_DIM >> 1);
    for (let ry = 0; ry < THUMB_DIM; ry++) {
      const row = ry * THUMB_DIM;
      for (let rx = 0; rx < THUMB_DIM; rx++) {
        const m = cells.peek(x0 + rx, y0 + ry);
        // One byte per cell is the whole storage argument, so a material id
        // that does not fit one reads as VOID rather than as a wrong colour.
        data[row + rx] = m >= 0 && m < 256 ? m : 0;
      }
    }
    this.storeThumb(event, data);
    return true;
  }

  /** How many captures are held. Bounded by `THUMB_CAP` by construction. */
  thumbCount(): number {
    return this.thumbs.size;
  }

  /**
   * Bytes of pixel data held. The store is fixed-size per entry and capped, so
   * this is exact rather than an estimate — docs/01 asks the save format to
   * justify its size and "bounded and measured" is the only honest way to.
   */
  thumbStoreBytes(): number {
    return this.thumbs.size * THUMB_DIM * THUMB_DIM;
  }

  /**
   * The store, as one string for `ProfileData.settings`.
   *
   * `settings` is already `Record<string, string | number | boolean>`, so this
   * needs no schema change in meta/ at all. Format is `v|dim|id:base64;…`; the
   * dimension is carried so a later retune of `THUMB_DIM` reads old saves as
   * stale rather than as garbage.
   */
  exportThumbs(): string {
    if (this.thumbs.size === 0) return '';
    const parts: string[] = [];
    for (const [id, data] of this.thumbs) parts.push(id + ':' + b64encode(data));
    return THUMB_FORMAT + '|' + THUMB_DIM + '|' + parts.join(';');
  }

  /** Inverse of `exportThumbs`. Returns how many entries were taken. */
  importThumbs(blob: string): number {
    if (!blob) return 0;
    const head = blob.indexOf('|');
    const second = blob.indexOf('|', head + 1);
    if (head < 0 || second < 0) return 0;
    if (blob.slice(0, head) !== THUMB_FORMAT) return 0;
    if (Number(blob.slice(head + 1, second)) !== THUMB_DIM) return 0;

    let taken = 0;
    for (const part of blob.slice(second + 1).split(';')) {
      if (this.thumbs.size >= THUMB_CAP) break;
      const colon = part.lastIndexOf(':');
      if (colon <= 0) continue;
      const data = b64decode(part.slice(colon + 1));
      if (data.length !== THUMB_DIM * THUMB_DIM) continue;
      this.storeThumb(part.slice(0, colon), data);
      taken++;
    }
    return taken;
  }

  /**
   * The view model is built once at store time, not per frame: `refreshCodex`
   * only ever assigns the same object back onto the same row, and `codex.ts`
   * caches its canvas by entry id.
   */
  private storeThumb(event: string, data: Uint8Array): void {
    this.thumbs.set(event, data);
    this.thumbVMs.set(event, {
      w: THUMB_DIM,
      h: THUMB_DIM,
      palette: this.thumbPalette,
      data,
    });
    this.lastCodexSig = -1;
  }
}

/**
 * `TrackUnlockVM` carries the display name, not the unlock id, because the UI
 * has no business knowing ids. The names are unique within a track, so this
 * lookup is exact — and it is built once per track rather than per query.
 */
const UNLOCK_ID_BY_NAME = new Map<string, string>();
for (const id of TRACK_IDS) {
  for (const u of unlocksForTrack(id)) UNLOCK_ID_BY_NAME.set(id + '\u0000' + u.name, u.id);
}

function unlockIdFor(track: TrackVM, name: string): string {
  return UNLOCK_ID_BY_NAME.get(track.id + '\u0000' + name) ?? '';
}

// ============================================================ the death screen
//
// docs/23. Everything below is a *projection* of state that already exists —
// `RunState`, `BankReport`, the run log — into `GameOverVM`. It records nothing
// and it computes no figure that the run does not already hold, which is the
// rule the rest of this file follows and the one a postmortem screen is most
// tempted to break: a "damage per second" or a "rooms per minute" invented here
// would be a statistic with no owner, agreeing with nothing.
//
// It is a free function rather than a `UIAdapter` method for one reason that
// matters: it must be callable, and testable, without a `World`, a `Wielder`, a
// document or a WebGL context. `tests/gameOver.test.ts` calls it directly.

/**
 * What the integrator hands over at the moment of death.
 *
 * Everything except `run` is optional, and every omission has a stated reading
 * — never a guessed value. That is the `UIFrameState` convention above, for the
 * same reason: a screen that quietly invents a zero is worse than one that says
 * it was not told.
 */
export interface GameOverInput {
  /** The run, **after** `endRun`. An `outcome` of null reads as an abandon. */
  run: RunState;
  /**
   * `bankRun`'s report. Omitted, the ledger section says the run was not banked
   * — which is a true and alarming thing to see, and better than a row of zeros
   * that looks like a run which earned nothing.
   */
  report?: BankReport | null;
  /**
   * `Game.events` at the instant of death, oldest first — **a copy**.
   *
   * The caller must snapshot rather than pass the live array. `Game.events` is a
   * 64-entry ring buffer and the simulation keeps running behind this screen
   * (that is the whole design), so a live reference would let a burning cave
   * push the death itself off the top of the list the player is reading.
   */
  log?: readonly string[];
  /** How many of those lines to show. The chain, not the run. */
  logLines?: number;
  /**
   * Display names for the ids the run stores. `world/` is not imported by this
   * file and is not going to be; the integrator already resolves the biome once
   * per frame, so passing the label through costs nothing and keeps the biome
   * table out of the seam. An id with no label prints as its id, which is ugly
   * and honest.
   */
  labels?: {
    biome?(id: string): string | undefined;
    enemy?(id: string): string | undefined;
    ability?(id: string): string | undefined;
    archetype?(id: string): string | undefined;
    kit?(id: string): string | undefined;
  };
  /**
   * What was in the player's hands and pack when it ended.
   *
   * Passed in rather than read off `run.inventory`, and this is a finding
   * rather than a preference: `addWeapon` and `addModule` exist in
   * `meta/runState.ts` and **had no call sites anywhere in `src/`**, so
   * `inventory.weapons`, `.modules`, `.mainHand` and `.offHand` were empty for
   * every run that has ever been played (#107). The live holdings are
   * `Game.runWeapons` / `Game.runModules`, which are combat-owned instances
   * this file must not reach for.
   *
   * **Still passed in, deliberately, while the ledger is only partly wired.**
   * `addModule` now has two real producers — a vendor purchase
   * (`world/vendor.ts`) and a craft (`meta/crafting.ts`), the two acquisition
   * paths that already own a `RunState` and can therefore record for
   * themselves. The other four (`equipKit`, the altar claim, the kill drop and
   * a stash withdrawal) and every `addWeapon` path live in `core/game.ts`, and
   * `tests/liveness.test.ts` holds the two-way entries that say so. Reading
   * `run.inventory` here before those land would print a *partial* ledger,
   * which is worse than an empty one: an incomplete list of what you found
   * still looks authoritative. This field goes away in the same change that
   * empties those entries.
   */
  carried?: {
    mainHand?: string | null;
    offHand?: string | null;
    /** Display names, in pickup order if the caller has one. */
    weapons?: readonly string[];
    modules?: readonly string[];
  };
  /** Unlock names this death raised, from `Progression.pendingUnlocks()`. */
  unlocks?: readonly string[];
  /** Is a "quit to title" exit wired? Same rule as `pauseMenuItems`. */
  hasQuitToTitle?: boolean;
}

const GAMEOVER_LOG_LINES = 8;

/** Title-case an id so an unlabelled `sunken_vault` still reads as a place. */
function prettyId(id: string): string {
  return id
    .replace(/[_-]+/g, ' ')
    .replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

function labelled(id: string, lookup?: (id: string) => string | undefined): string {
  return lookup?.(id) ?? prettyId(id);
}

/** `8f2a1` — the same base-36 shorthand `makeRunId` uses, so the two agree. */
function seedTag(seed: number): string {
  return (seed >>> 0).toString(36);
}

const OUTCOME_TITLE: Readonly<Record<string, string>> = {
  death: 'You Died',
  extract: 'Extracted',
  abandon: 'Run Abandoned',
};

/**
 * The postmortem, built once.
 *
 * Section order is the argument of docs/23 §4 and is not arbitrary: **cause,
 * then route, then violence, then what you were holding, then what it bought
 * you.** It descends from the sentence the player is looking for to the numbers
 * they will read only if the sentence held them, which is the opposite of the
 * usual stat-sheet order and the one Noita's screen gets right.
 */
export function buildGameOverVM(input: GameOverInput): GameOverVM {
  const { run, report, labels, carried } = input;
  const outcome = run.outcome;
  const kind = outcome?.kind ?? 'abandon';

  const biomeId = outcome?.biome || run.currentBiome;
  const biomeName = biomeId ? labelled(biomeId, labels?.biome) : 'Nowhere';

  const cause = outcome?.cause ?? null;

  const sections: GameOverSectionVM[] = [];

  // ---- where and how far ---------------------------------------------------
  const route: GameOverRowVM[] = [
    { key: 'time', label: 'Time', value: formatRunTime(run.elapsedMs) },
    { key: 'depth', label: 'Biomes reached', value: String(run.biomeOrder.length) },
    { key: 'rooms', label: 'Rooms seen', value: String(run.rooms.visited.size) },
    { key: 'archetype', label: 'Body', value: labelled(run.archetype, labels?.archetype) },
    { key: 'kit', label: 'Kit', value: labelled(run.kit, labels?.kit) },
    {
      key: 'world',
      label: 'World',
      value: run.worldMode,
      note: run.modifiers.length ? run.modifiers.map(prettyId).join(', ') : undefined,
    },
  ];
  if (run.biomeOrder.length > 1) {
    route.splice(2, 0, {
      key: 'route',
      label: 'Route',
      value: String(run.biomeOrder.length),
      note: run.biomeOrder.map((b) => labelled(b, labels?.biome)).join(' → '),
    });
  }
  sections.push({ id: 'route', title: 'The Descent', rows: route, empty: 'no ground covered' });

  // ---- violence ------------------------------------------------------------
  const s = run.stats;
  const violence: GameOverRowVM[] = [];
  if (s.kills > 0) {
    // The three names you killed most, so "42 kills" becomes a sentence about
    // what this run actually fought.
    const top = Object.entries(s.killsBy)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 3)
      .map(([id, n]) => `${labelled(id, labels?.enemy)} ×${n}`);
    violence.push({ key: 'kills', label: 'Kills', value: String(s.kills), note: top.join(', ') });
  }
  if (s.bossesKilled.length > 0) {
    violence.push({
      key: 'bosses',
      label: 'Bosses',
      value: String(s.bossesKilled.length),
      note: s.bossesKilled.map((b) => labelled(b, labels?.enemy)).join(', '),
      tone: 'good',
    });
  }
  if (s.elitesKilled.length > 0) {
    violence.push({
      key: 'elites',
      label: 'Elites',
      value: String(s.elitesKilled.length),
      note: s.elitesKilled.map((e) => labelled(e, labels?.enemy)).join(', '),
    });
  }
  if (s.damageDealt > 0) {
    violence.push({ key: 'dealt', label: 'Damage dealt', value: String(Math.round(s.damageDealt)) });
  }
  if (s.damageTaken > 0) {
    violence.push({
      key: 'taken',
      label: 'Damage taken',
      value: String(Math.round(s.damageTaken)),
      tone: 'bad',
    });
  }
  if (s.cellsDestroyed > 0) {
    violence.push({
      key: 'dug',
      label: 'Cells destroyed',
      value: String(s.cellsDestroyed),
      note: 'the world remembers this much of you',
    });
  }
  sections.push({
    id: 'violence',
    title: 'What You Did',
    rows: violence,
    empty: 'you killed nothing and broke nothing',
  });

  // ---- what you were holding ----------------------------------------------
  const held: GameOverRowVM[] = [];
  const hands = [carried?.mainHand, carried?.offHand].filter((h): h is string => !!h);
  if (hands.length > 0) {
    held.push({ key: 'hands', label: 'In hand', value: hands.join(' / ') });
  }
  const packWeapons = carried?.weapons ?? [];
  if (packWeapons.length > 0) {
    held.push({
      key: 'weapons',
      label: 'Weapons found',
      value: String(packWeapons.length),
      note: packWeapons.join(', '),
    });
  }
  const packModules = carried?.modules ?? [];
  if (packModules.length > 0) {
    held.push({
      key: 'modules',
      label: 'Modules',
      value: String(packModules.length),
      note: packModules.map(prettyId).join(', '),
    });
  }
  if (run.traversal.found.length > 0) {
    held.push({
      key: 'abilities',
      label: 'Abilities found',
      value: String(run.traversal.found.length),
      note: run.traversal.found.map((a) => labelled(a, labels?.ability)).join(', '),
      tone: 'good',
    });
  }
  const pouch = Object.entries(run.inventory.materials).filter(([, n]) => n > 0);
  if (pouch.length > 0) {
    const total = pouch.reduce((n, [, v]) => n + v, 0);
    held.push({
      key: 'pouch',
      label: 'Pouch',
      value: String(total),
      note: pouch
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([m, n]) => `${prettyId(m)} ${n}`)
        .join(', '),
      // Run-scoped by design. It dies here whatever the outcome, so it is a
      // loss on the screen even after a clean extraction.
      tone: kind === 'death' ? 'bad' : 'plain',
    });
  }
  if (run.discoveries.length > 0) {
    held.push({
      key: 'reactions',
      label: 'Reactions seen',
      value: String(run.discoveries.length),
      note: run.discoveries.slice(0, 6).map(prettyId).join(', '),
    });
  }
  sections.push({
    id: 'carried',
    title: 'What You Carried',
    rows: held,
    empty: 'you took nothing out of this world',
  });

  // ---- standing ------------------------------------------------------------
  const standing: GameOverRowVM[] = [];
  const f = new Factions(run.factions);
  for (const id of FACTION_IDS) {
    const rep = f.reputation(id);
    if (rep === 0) continue;
    standing.push({
      key: `rep-${id}`,
      label: FACTION_BY_ID[id].name,
      value: `${rep > 0 ? '+' : ''}${rep}`,
      note: f.standing(id),
      tone: rep > 0 ? 'good' : 'bad',
    });
  }
  sections.push({
    id: 'standing',
    title: 'Who Noticed',
    rows: standing,
    empty: 'nobody took a side',
  });

  // ---- what the death bought ----------------------------------------------
  // Last, and last on purpose. This is the half of the screen that says death
  // is progress here rather than a reset (docs/05 §4), and it is the argument
  // for pressing Continue — so it sits directly above the button.
  const ledger: GameOverRowVM[] = [];
  if (report) {
    // Both figures, because they differ and the difference *is* the death
    // penalty: `recordRunEnd` banks a fraction of what the run carried, and a
    // screen that showed only the banked number would hide the whole cost of
    // dying rather than extracting.
    const earned = run.inventory.ash;
    ledger.push({
      key: 'ash',
      label: 'Ash banked',
      value: String(report.ash),
      note: earned > report.ash ? `of ${earned} carried — the rest died with you` : undefined,
      tone: report.ash > 0 ? 'good' : 'plain',
    });
    if (report.ashCarried > 0) {
      ledger.push({
        key: 'carried-ash',
        label: 'Ash carried through',
        value: String(report.ashCarried),
        note: "the Courier's cut — it is in your pocket next run",
        tone: 'good',
      });
    }
    if (report.scripLost > 0) {
      ledger.push({
        key: 'scrip',
        label: 'Scrip lost',
        value: String(report.scripLost),
        note: 'spend it next time',
        tone: 'bad',
      });
    }
    if (report.lineage.archetype && report.lineage.amount > 0) {
      ledger.push({
        key: 'lineage',
        label: 'Lineage',
        value: `+${report.lineage.amount}`,
        note: `toward ${labelled(report.lineage.archetype, labels?.archetype)}`,
        tone: 'good',
      });
    }
    if (report.codexNew > 0) {
      ledger.push({ key: 'codex', label: 'Codex entries', value: `+${report.codexNew}` });
    }
    if (report.mapEchoed) {
      ledger.push({
        key: 'echo',
        label: 'Route remembered',
        value: 'yes',
        note: 'the Echo keeps this map for this seed',
        tone: 'good',
      });
    }
  }
  const unlocks = input.unlocks ?? [];
  for (const u of unlocks) {
    ledger.push({ key: `unlock-${u}`, label: 'Unlocked', value: prettyId(u), tone: 'good' });
  }
  sections.push({
    id: 'ledger',
    title: 'What It Bought',
    rows: ledger,
    // Two different silences, and they must not print the same sentence: a run
    // that banked nothing is a fact about the run, a run that was never banked
    // is a fact about the code.
    empty: report ? 'this death paid for nothing' : 'the run was not banked',
  });

  const logAll = input.log ?? [];
  const keep = Math.max(0, input.logLines ?? GAMEOVER_LOG_LINES);

  const title = OUTCOME_TITLE[kind] ?? 'Run Over';

  return {
    title,
    // The screen deliberately reuses `causeHeadline` — the *same string the
    // toast just said*, not a third wording of it. The toast fades after 4.5 s;
    // this is that sentence declining to fade, which is why it must be the same
    // sentence and not a paraphrase of it.
    cause: cause ? causeHeadline(cause) : null,
    causeDetail: cause ? causeSentence(cause) : null,
    epitaph: [
      biomeName,
      formatRunTime(run.elapsedMs),
      `${runAbilities(run).length} abilities`,
      `seed ${seedTag(run.seed)}`,
    ].join(' · '),
    sections,
    log: keep === 0 ? [] : logAll.slice(-keep),
    actions: gameOverActions({ quitToTitle: input.hasQuitToTitle === true }),
  };
}
